import {
  type MealKey,
  type Recipe,
  type MealBlock,
  mealCaloriePercent,
  mealOrder,
  buildScaledRecipeId,
  findRecipeById,
  parseScaledRecipeId,
  extractGrams,
} from "@/data/recipes";
import type { MealSelection } from "@/contexts/DietContext";
import {
  type MacroTarget,
  macroScore,
  solveStructuredMealScales,
  solveSimpleMealScales,
  applyScales,
  getScaleLimits,
  getRecipeGramLimits,
  isLowCalDensityRecipe,
} from "@/lib/macroSolver";
import {
  getNormalizedMealBudgets as getExactMealBudgets,
  rebalanceMealOptionToTarget,
  closeMealOptionToBudget,
} from "@/lib/dietOptionRebalance";

// ═══════════════════════════════════════════════════════════
// §1 — TYPES & INTERFACES
// ═══════════════════════════════════════════════════════════

interface GeneratorInput {
  preferences: Record<MealKey, string[]>;
  targetCalories: number;
  macroTargets?: MacroTarget;
  optionsCount: number;
  recipes: Record<MealKey, MealBlock>;
}

interface Candidate {
  ids: string[];
  cal: number;
  protein: number;
  carbs: number;
  fat: number;
  proteinCount: number;
  carbCount: number;
  legumeCount: number;
  saladCount: number;
  prepCount: number;
}

interface MealCapacity {
  protein: number;
  carbs: number;
  fat: number;
  cal: number;
}

interface GlobalScaleVar {
  mealKey: MealKey;
  recipeId: string;
  protein: number;
  carbs: number;
  fat: number;
  scale: number;
  minScale: number;
  maxScale: number;
}

interface OptionState {
  meals: Record<MealKey, string[]>;
}

// ═══════════════════════════════════════════════════════════
// §2 — CONSTANTS & EXCLUSION RULES
// ═══════════════════════════════════════════════════════════

const EXCLUSION_KEYWORDS: string[][] = [
  ["iogurte", "leite", "vitamina", "cafe_leite"],
  [
    "cafe_puro",
    "cafe_leite",
    "leite_desnatado_cafe",
    "leite_desnatado_lm",
    "leite_desnatado_tarde",
    "suco_verde_cafe",
    "suco_verde_tarde",
    "vitamina",
    "vitamina_tarde",
  ],
  ["pasta_amendoim", "pasta de amendoim"],
  ["whey"],
  ["cuscuz"],
  ["frango"],
  ["carne"],
  ["peixe", "tilapia", "salmão", "sardinha"],
  ["omelete", "panqueca", "pao_ovo", "cuscuz_ovo", "cuscuz_ovo_queijo", "cuscuz_frango", "pão + ovo", "cuscuz + ovo", "tapioca", "suco_verde_omelete"],
  ["pao_ovo", "pao_queijo", "sanduiche_natural"],
  ["aveia_banana", "panqueca_banana"],
  ["inhame", "mandioca", "batata_doce", "batata_inglesa"],
  ["arroz_feijao_almoco", "feijao_preto"],
  ["arroz_feijao_jt", "feijao_preto_jt"],
];

const BEVERAGE_KEYWORDS: string[] = ["cafe_puro", "cafe_leite", "leite_desnatado", "suco_verde", "vitamina"];

const HEAVY_KEYWORDS: string[] = [
  "suco_verde_omelete",
  "cuscuz_ovo",
  "cuscuz_ovo_queijo",
  "cuscuz_frango",
  "cuscuz_queijo",
  "cuscuz_carne",
  "omelete",
  "panqueca",
  "tapioca",
  "pao_ovo",
  "pao_queijo",
  "vitamina",
  "acai",
];

const INGREDIENT_MAX_REPEAT = 2;

const TRACKED_INGREDIENTS: string[] = [
  "banana",
  "maçã",
  "laranja",
  "morango",
  "mamão",
  "melão",
  "melancia",
  "abacaxi",
  "uva",
  "pêra",
  "kiwi",
  "caqui",
  "bergamota",
  "aveia",
  "ovo",
  "frango",
  "carne",
  "feijão",
];

const DIRECT_OVERLAP_GROUPS: [string, string[]][] = [
  ["arroz", ["arroz"]],
  ["feijao", ["feijão", "feijao"]],
  ["macarrao", ["macarrão", "macarrao"]],
  ["mandioca", ["mandioca"]],
  ["inhame", ["inhame"]],
  ["batata_doce", ["batata doce"]],
  ["batata_inglesa", ["batata inglesa"]],
  ["cuscuz", ["cuscuz"]],
  ["tapioca", ["tapioca", "goma de tapioca"]],
  ["polenta", ["polenta"]],
  ["milho", ["milho"]],
  ["aveia", ["aveia"]],
  ["granola", ["granola"]],
  ["pasta_amendoim", ["pasta de amendoim"]],
  ["mel", ["mel"]],
  ["queijo", ["queijo"]],
  ["frango", ["frango"]],
  ["carne", ["carne bovina", "carne moída", "carne moida", "carne"]],
  ["peixe", ["peixe", "tilápia", "tilapia", "salmão", "salmao", "sardinha"]],
  ["ovo", ["ovo"]],
  ["iogurte", ["iogurte"]],
  ["leite", ["leite"]],
];

const IGNORE_OVERLAP_KEYWORDS = [
  "azeite",
  "orégano",
  "oregano",
  "canela",
  "sal",
  "água",
  "agua",
  "limão",
  "limao",
  "cebola",
  "alho",
  "requeijão",
  "requeijao",
  "maionese",
  "pitada",
];

const PROTEIN_FAMILIES: [string, string[]][] = [
  ["carne", ["carne", "patinho", "bovina", "lombo"]],
  ["frango", ["frango", "peru"]],
  ["peixe", ["peixe", "tilapia", "tilápia", "salmão", "sardinha"]],
  ["ovo", ["ovo", "omelete"]],
];

const LOW_CAL_BEVERAGE_THRESHOLD = 20;
const BEVERAGE_INGREDIENT_KEYWORDS = ["café", "chá", "água"];

const GRAIN_KEYWORDS = ["arroz", "macarrão", "cuscuz", "polenta", "milho"];
const LEGUME_KEYWORDS = ["feijão", "feijao", "lentilha", "ervilha", "grão-de-bico", "grao-de-bico", "grão de bico"];
const PROTEIN_KEYWORDS_FLOOR = [
  "frango",
  "carne",
  "peixe",
  "tilápia",
  "salmão",
  "sardinha",
  "lombo",
  "peru",
  "patinho",
];

const INGREDIENT_FLOORS: [string, number][] = [
  ["aveia", 20],
  ["granola", 15],
  ["pasta de amendoim", 10],
  ["queijo", 20],
  ["mel", 5],
];

const MAX_SIMPLE_ITEMS = 3;
const MAX_COMBINATIONS = 80;

/**
 * Compute normalized meal calorie budgets based on active meals.
 * This is the SINGLE source of truth for per-meal budgets.
 */
function getNormalizedMealBudgets(
  targetCalories: number,
  activeMeals: MealKey[],
): Record<MealKey, number> {
  return getExactMealBudgets(targetCalories, activeMeals);
}

/**
 * Hard-clamp every meal in an option to maxRatio * budget.
 * Uses real resolved calories (not linear approximation).
 */
function clampMealsToBudgetCeiling(
  allOptions: Record<MealKey, string[][]>,
  activeMeals: MealKey[],
  oIdx: number,
  budgets: Record<MealKey, number>,
  recipes: Record<MealKey, MealBlock>,
  preferences: Record<MealKey, string[]>,
  maxRatio = 1.10,
): void {
  for (const mealKey of activeMeals) {
    const mealIds = allOptions[mealKey][oIdx] || [];
    if (mealIds.length === 0) continue;
    if (mealIds.length === 1 && (preferences[mealKey] || []).length === 1) continue;
    const budget = budgets[mealKey];
    if (!budget || budget <= 0) continue;

    let mealCal = 0;
    for (const sid of mealIds) {
      const r = safeResolveRecipe(sid, recipes);
      // Salads excluded from calorie budgets
      if (r && !isSaladRecipe(r)) mealCal += r.cal;
    }
    if (mealCal <= 0) continue;

    const ceiling = budget * maxRatio;
    if (mealCal <= ceiling) continue;

    // Scale down uniformly (skip salads)
    const correction = ceiling / mealCal;
    for (let i = 0; i < mealIds.length; i++) {
      const { baseId, scale: currentScale, isFree } = safeParseScaledId(mealIds[i]);
      if (isFree) continue;
      const base = findRecipeById(baseId, recipes);
      if (!base || isSaladRecipe(base)) continue;
      const { minScale, maxScale } = getScaleLimits(base);
      const { effectiveMin } = getEffectiveLimits(base, currentScale, minScale, maxScale);
      const newScale = Math.max(effectiveMin, currentScale * correction);
      mealIds[i] = buildScaledRecipeId(baseId, newScale);
    }
  }
}

/**
 * Last-resort enforcement: snacks/optional with effectiveMin too high
 * (e.g. nutella 30g = 167kcal vs 90kcal budget) cannot be reduced by
 * clampMealsToBudgetCeiling. This function:
 *   1. Identifies non-structured meals (snacks, morning_snack, optional) above maxRatio.
 *   2. Removes the offending high-calorie addition (largest item whose
 *      effectiveMin*cal alone is > 50% of budget, or the most recently added).
 *   3. Re-clamps the remaining items.
 */
function enforceMealHardCeiling(
  allOptions: Record<MealKey, string[][]>,
  activeMeals: MealKey[],
  oIdx: number,
  budgets: Record<MealKey, number>,
  recipes: Record<MealKey, MealBlock>,
  preferences: Record<MealKey, string[]>,
  maxRatio = 1.10,
): void {
  // Breakfast/lunch/dinner are STRUCTURED — never remove items, only scale.
  // Only snacks and optional are subject to item removal as last resort.
  const NON_STRUCTURED: MealKey[] = ["morning_snack", "snack", "optional"];
  const REMOVABLE_CATEGORIES = new Set(["dessert", "treat", "spread"]);

  for (const mealKey of activeMeals) {
    if (!NON_STRUCTURED.includes(mealKey)) continue;
    const mealIds = allOptions[mealKey][oIdx];
    if (!mealIds || mealIds.length <= 1) continue;
    if ((preferences[mealKey] || []).length <= 1) continue; // locked
    const budget = budgets[mealKey];
    if (!budget || budget <= 0) continue;
    const ceiling = budget * maxRatio;
    const minMealFloor = budget * 0.40; // Never empty meal below 40% of budget

    // Up to 3 attempts: remove offending item, then re-clamp
    for (let attempt = 0; attempt < 3; attempt++) {
      let mealCal = 0;
      for (const sid of mealIds) {
        const r = safeResolveRecipe(sid, recipes);
        if (r && !isSaladRecipe(r)) mealCal += r.cal;
      }
      if (mealCal <= ceiling) break;

      // Find offender — STRICT criteria: must be either
      //   (a) min-portion alone > 60% of budget (impossible to fit), OR
      //   (b) category in dessert/treat/spread (clearly non-structural)
      let offenderIdx = -1;
      let offenderScore = -Infinity;
      let offenderItemCal = 0;
      for (let i = 0; i < mealIds.length; i++) {
        const { baseId, isFree } = safeParseScaledId(mealIds[i]);
        if (isFree) continue;
        const base = findRecipeById(baseId, recipes);
        if (!base || isSaladRecipe(base)) continue;
        if (isLowCalBeverage(base)) continue;
        const { minScale, maxScale } = getScaleLimits(base);
        const { effectiveMin } = getEffectiveLimits(base, 1.0, minScale, maxScale);
        const minCal = base.cal * effectiveMin;
        const cat = ((base as { category?: string }).category || "").toLowerCase();
        const isHardCap = minCal > budget * 0.6;
        const isRemovableCat = REMOVABLE_CATEGORIES.has(cat);
        // STRICT: item must satisfy at least one of (a) or (b)
        if (!isHardCap && !isRemovableCat) continue;
        const r = safeResolveRecipe(mealIds[i], recipes);
        const itemCal = r && !isSaladRecipe(r) ? r.cal : base.cal;
        // Safeguard: skip if removing would drop the meal below 40% of budget
        if (mealCal - itemCal < minMealFloor) continue;
        const score = (isHardCap ? 1000 : 0) + (isRemovableCat ? 500 : 0) + base.cal + i * 0.01;
        if (score > offenderScore) {
          offenderScore = score;
          offenderIdx = i;
          offenderItemCal = itemCal;
        }
      }

      if (offenderIdx < 0) break;
      mealIds.splice(offenderIdx, 1);
      // Immediately try to recompose the option locally so we never leave the
      // meal subnourished (e.g. 23 kcal of 306 kcal budget). The closer will
      // pull a compatible item from the user's preferences if needed.
      closeOptionToMealBudgetInPlace(allOptions, mealKey, oIdx, budgets, recipes, preferences);
    }

    // Final pass: scale down remaining items to fit ceiling
    clampMealsToBudgetCeiling(allOptions, activeMeals, oIdx, budgets, recipes, preferences, maxRatio);
  }
}

/**
 * Run the exact-closure helper on a single meal option, mutating `allOptions`
 * in place. Uses the user's preferences for that meal as the candidate pool
 * so the algorithm can append a compatible item when the remaining ones cannot
 * structurally reach the budget.
 */
function closeOptionToMealBudgetInPlace(
  allOptions: Record<MealKey, string[][]>,
  mealKey: MealKey,
  oIdx: number,
  budgets: Record<MealKey, number>,
  recipes: Record<MealKey, MealBlock>,
  preferences: Record<MealKey, string[]>,
): void {
  const optionIds = allOptions[mealKey]?.[oIdx];
  if (!optionIds || optionIds.length === 0) return;
  const budget = budgets[mealKey];
  if (!budget || budget <= 0) return;

  const block = recipes[mealKey];
  const preferenceIds = (preferences[mealKey] || []).filter((id) => Boolean(findRecipeById(id, recipes)));
  // Lunch/dinner have rigid structure — never inject extra items via the closer.
  const allowAdditions = mealKey !== "lunch" && mealKey !== "dinner";
  // Snacks and optional must converge tightly so that all options of the
  // same meal land at virtually identical kcal (Bug 1: avoid divergent
  // option kcal in snacks/optional/sweets meals).
  const isTight = mealKey === "morning_snack" || mealKey === "snack" || mealKey === "optional";

  const closed = closeMealOptionToBudget({
    optionIds,
    targetCalories: budget,
    resolveRecipe: (id) => safeResolveRecipe(id, recipes),
    availablePreferences: allowAdditions && block ? preferenceIds : [],
    maxAdditions: 1,
    // Tight band [99%, 102%] for snacks/optional → equal kcal between options;
    // looser band [99%, 105%] for breakfast (composite recipes).
    maxOverRatio: isTight ? 1.02 : 1.05,
    acceptRatio: isTight ? 0.99 : 0.99,
  });

  allOptions[mealKey][oIdx] = closed;
}

/**
 * Hard daily clamp — proportionally scales ALL non-free, non-salad items down
 * until daily total ≤ targetCalories * maxRatio. Iterates until convergence
 * (items hitting their effective minimum are excluded from further scaling).
 *
 * This is the LAST line of defense to guarantee 100% closure.
 */
function hardDailyClamp(
  allOptions: Record<MealKey, string[][]>,
  activeMeals: MealKey[],
  oIdx: number,
  targetCalories: number,
  recipes: Record<MealKey, MealBlock>,
  preferences: Record<MealKey, string[]>,
  maxRatio = 1.001,
): void {
  if (targetCalories <= 0) return;
  const ceiling = targetCalories * maxRatio;

  for (let iter = 0; iter < 12; iter++) {
    const totalCal = computeDiscreteDailyCalories(allOptions, activeMeals, oIdx, recipes);
    if (totalCal <= ceiling) return;

    // Compute scalable mass = sum of cal of items NOT yet at their min
    interface Scalable {
      mealKey: MealKey;
      itemIdx: number;
      baseId: string;
      currentScale: number;
      effectiveMin: number;
      effectiveMax: number;
      currentCal: number;
    }
    const scalables: Scalable[] = [];
    let scalableMass = 0;
    let fixedMass = 0;

    for (const mealKey of activeMeals) {
      const mealIds = allOptions[mealKey][oIdx] || [];
      const isLockedSingle = mealIds.length === 1 && (preferences[mealKey] || []).length === 1;
      for (let i = 0; i < mealIds.length; i++) {
        const { baseId, scale, isFree } = safeParseScaledId(mealIds[i]);
        const base = findRecipeById(baseId, recipes);
        if (!base) continue;
        if (isFree || isSaladRecipe(base)) continue;
        if (isLockedSingle) {
          fixedMass += base.cal;
          continue;
        }
        const { minScale, maxScale } = getScaleLimits(base);
        const { effectiveMin, effectiveMax } = getEffectiveLimits(base, scale, minScale, maxScale);
        // Recipe stored cal already reflects current scale (cal is per-portion at current scale)
        // Actually base.cal is the BASE recipe; scaledId encodes the scale and findRecipeById returns scaled.
        // Use the resolved (scaled) cal:
        const resolved = safeResolveRecipe(mealIds[i], recipes);
        const currentCal = resolved?.cal || 0;
        if (scale <= effectiveMin + 1e-6) {
          fixedMass += currentCal;
        } else {
          scalables.push({ mealKey, itemIdx: i, baseId, currentScale: scale, effectiveMin, effectiveMax, currentCal });
          scalableMass += currentCal;
        }
      }
    }

    if (scalableMass <= 0) return; // nothing to scale, give up

    // We need: fixedMass + scalableMass * factor <= ceiling
    const targetScalableMass = Math.max(0, ceiling - fixedMass);
    const factor = targetScalableMass / scalableMass;
    if (factor >= 1) return;

    let progressed = false;
    for (const s of scalables) {
      const proposedScale = s.currentScale * factor;
      const newScale = Math.max(s.effectiveMin, proposedScale);
      if (Math.abs(newScale - s.currentScale) < 1e-6) continue;
      allOptions[s.mealKey][oIdx][s.itemIdx] = buildScaledRecipeId(s.baseId, newScale);
      progressed = true;
    }
    if (!progressed) return;
  }
}

/**
 * Hard daily BOOST — proportionally scales ALL non-free, non-salad items UP
 * until daily total ≥ targetCalories * minRatio. Iterates until convergence
 * (items hitting their effective MAX are excluded from further scaling).
 *
 * Mirror of hardDailyClamp for the lower bound. Guarantees diet doesn't fall
 * below 99% of target when there's headroom in any item.
 */
function hardDailyBoost(
  allOptions: Record<MealKey, string[][]>,
  activeMeals: MealKey[],
  oIdx: number,
  targetCalories: number,
  recipes: Record<MealKey, MealBlock>,
  preferences: Record<MealKey, string[]>,
  minRatio = 0.999,
  mealBudgets?: Record<MealKey, number>,
): void {
  if (targetCalories <= 0) return;
  const floor = targetCalories * minRatio;

  for (let iter = 0; iter < 12; iter++) {
    const totalCal = computeDiscreteDailyCalories(allOptions, activeMeals, oIdx, recipes);
    if (totalCal >= floor) return;

    interface Scalable {
      mealKey: MealKey;
      itemIdx: number;
      baseId: string;
      currentScale: number;
      effectiveMin: number;
      effectiveMax: number;
      currentCal: number;
      baseCal: number;
      mealCapHeadroom: number; // cap on additional calories this item can add
    }
    const scalables: Scalable[] = [];
    let scalableMass = 0;
    let fixedMass = 0;

    // Pre-compute per-meal current totals (non-free, non-salad)
    const mealCurrentTotals: Partial<Record<MealKey, number>> = {};
    for (const mealKey of activeMeals) {
      let mt = 0;
      const ids = allOptions[mealKey][oIdx] || [];
      for (const sid of ids) {
        const r = safeResolveRecipe(sid, recipes);
        if (!r || isSaladRecipe(r)) continue;
        const { isFree } = safeParseScaledId(sid);
        if (isFree) continue;
        mt += r.cal;
      }
      mealCurrentTotals[mealKey] = mt;
    }

    for (const mealKey of activeMeals) {
      const mealIds = allOptions[mealKey][oIdx] || [];
      const isLockedSingle = mealIds.length === 1 && (preferences[mealKey] || []).length === 1;
      const mealBudget = mealBudgets?.[mealKey] || 0;
      const mealCap = mealBudget > 0 ? mealBudget * 1.08 : Infinity;
      const mealCurrent = mealCurrentTotals[mealKey] || 0;
      // headroom = how many extra kcal we can still add to this meal before hitting the cap
      const mealHeadroom = Math.max(0, mealCap - mealCurrent);

      for (let i = 0; i < mealIds.length; i++) {
        const { baseId, scale, isFree } = safeParseScaledId(mealIds[i]);
        const base = findRecipeById(baseId, recipes);
        if (!base) continue;
        if (isFree || isSaladRecipe(base)) continue;
        if (isLockedSingle) {
          fixedMass += base.cal;
          continue;
        }
        const { minScale, maxScale } = getScaleLimits(base);
        const { effectiveMin, effectiveMax } = getEffectiveLimits(base, scale, minScale, maxScale);
        const resolved = safeResolveRecipe(mealIds[i], recipes);
        const currentCal = resolved?.cal || 0;
        // Limit growth so we don't push the meal past 108% of its budget
        const baseCal = base.cal;
        const maxScaleByMealCap = baseCal > 0 ? scale + mealHeadroom / baseCal : effectiveMax;
        const cappedMax = Math.min(effectiveMax, maxScaleByMealCap);
        if (cappedMax <= scale + 1e-6) {
          fixedMass += currentCal;
        } else {
          scalables.push({
            mealKey,
            itemIdx: i,
            baseId,
            currentScale: scale,
            effectiveMin,
            effectiveMax: cappedMax,
            currentCal,
            baseCal,
            mealCapHeadroom: mealHeadroom,
          });
          scalableMass += currentCal;
        }
      }
    }

    if (scalableMass <= 0) return; // no headroom anywhere
    const targetScalableMass = Math.max(0, floor - fixedMass);
    const factor = targetScalableMass / scalableMass;
    if (factor <= 1) return;

    let progressed = false;
    for (const s of scalables) {
      const proposedScale = s.currentScale * factor;
      const newScale = Math.min(s.effectiveMax, proposedScale);
      if (Math.abs(newScale - s.currentScale) < 1e-6) continue;
      allOptions[s.mealKey][oIdx][s.itemIdx] = buildScaledRecipeId(s.baseId, newScale);
      progressed = true;
    }
    if (!progressed) return;
  }
}

/**
 * Add items from user preferences to meals that have a calorie deficit AND
 * cannot grow further (all current items at effectiveMax).
 *
 * Skips lunch/dinner (rigid structure: protein+carb+legume+salad).
 * Skips meals with only one preference (locked single-item meal).
 * Adds items whose calories fit the remaining gap (30%–110% of deficit),
 * respect conflict rules, and aren't already present.
 *
 * Runs BEFORE hardDailyBoost so the boost can fine-tune the new total.
 */
function addItemsToFillMealDeficit(
  allOptions: Record<MealKey, string[][]>,
  activeMeals: MealKey[],
  oIdx: number,
  mealBudgets: Record<MealKey, number>,
  recipes: Record<MealKey, MealBlock>,
  preferences: Record<MealKey, string[]>,
  minRatio = 0.90,
): void {
  for (const mealKey of activeMeals) {
    // Skip rigid structured meals and optional (strict budget)
    if (mealKey === "lunch" || mealKey === "dinner" || mealKey === "optional") continue;
    const prefs = preferences[mealKey] || [];
    if (prefs.length <= 1) continue; // locked single

    const mealIds = allOptions[mealKey][oIdx];
    if (!mealIds) continue;
    const budget = mealBudgets[mealKey] || 0;
    if (budget < 120) continue; // skip tiny budgets where one item already saturates

    const mealRecipes = recipes[mealKey]?.items || [];

    // Try up to 3 additions per meal
    for (let attempt = 0; attempt < 3; attempt++) {
      // Compute current real meal calories (exclude :free and salads)
      let mealCal = 0;
      let allAtMax = true;
      for (const sid of mealIds) {
        const { baseId, scale, isFree } = safeParseScaledId(sid);
        const base = findRecipeById(baseId, recipes);
        if (!base) continue;
        if (isFree || isSaladRecipe(base)) continue;
        const resolved = safeResolveRecipe(sid, recipes);
        mealCal += resolved?.cal || 0;
        const { minScale, maxScale } = getScaleLimits(base);
        const { effectiveMax } = getEffectiveLimits(base, scale, minScale, maxScale);
        if (scale < effectiveMax - 1e-3) allAtMax = false;
      }

      const floor = budget * minRatio;
      if (mealCal >= floor) break;
      // Only add new items when we cannot scale further; otherwise hardDailyBoost handles it
      if (!allAtMax) break;

      const deficit = floor - mealCal;
      if (deficit < 20) break; // not worth adding

      // Find a candidate from preferences not already present
      const presentIds = new Set(mealIds.map(sid => safeParseScaledId(sid).baseId));
      let bestCandidate: { id: string; cal: number; fitScore: number } | null = null;

      // Hard ceiling: any addition must keep meal at ≤ 108% of budget at smallest realistic portion
      const mealCeiling = budget * 1.08;
      for (const prefId of prefs) {
        if (presentIds.has(prefId)) continue;
        const cand = findRecipeById(prefId, recipes);
        if (!cand) continue;
        if (isLowCalBeverage(cand)) continue; // beverages handled by forceIncludeBeverages
        if (isSaladRecipe(cand)) continue;
        if (hasConflict([...Array.from(presentIds), prefId], mealRecipes)) continue;

        const candCal = cand.cal;
        if (candCal < deficit * 0.3) continue;

        // Compute the smallest realistic portion this candidate can take.
        // If even at its minimum it would push the meal above 108% budget, reject.
        const { minScale, maxScale } = getScaleLimits(cand);
        const { effectiveMin } = getEffectiveLimits(cand, 1.0, minScale, maxScale);
        const minCandCal = candCal * effectiveMin;
        if (mealCal + minCandCal > mealCeiling) continue;
        // Also reject if min-portion exceeds the deficit + 5% of budget
        if (minCandCal > deficit + budget * 0.05) continue;

        // Score by closeness to deficit (using the realistic min-portion)
        const fitScore = Math.abs(minCandCal - deficit);
        if (!bestCandidate || fitScore < bestCandidate.fitScore) {
          bestCandidate = { id: prefId, cal: candCal, fitScore };
        }
      }

      if (!bestCandidate) break;
      // Append at base scale (1.0). Subsequent hardDailyBoost / hardDailyClamp / clampMealsToBudgetCeiling
      // will fine-tune the scale to fit.
      mealIds.push(buildScaledRecipeId(bestCandidate.id, 1.0));
    }
  }
}

/**
 * Clone a meal option with slight scale variation (±3%) to avoid identical clones.
 */
function varyOptionScales(source: string[], recipes: Record<MealKey, MealBlock>): string[] {
  return source.map(scaledId => {
    const { baseId, scale, isFree } = safeParseScaledId(scaledId);
    if (isFree) return scaledId;
    const base = findRecipeById(baseId, recipes);
    if (!base) return scaledId;
    const { minScale, maxScale } = getScaleLimits(base);
    const { effectiveMin, effectiveMax } = getEffectiveLimits(base, scale, minScale, maxScale);
    const variation = 0.97 + Math.random() * 0.06; // 0.97 to 1.03
    const lowerBound = Math.max(effectiveMin, scale * 0.97);
    const upperBound = Math.min(effectiveMax, scale * 1.03);
    const newScale = clamp(scale * variation, lowerBound, Math.max(lowerBound, upperBound));
    return buildScaledRecipeId(baseId, newScale);
  });
}

/**
 * Classify a recipe's role for swap purposes (protein↔protein, etc).
 */
function getRecipeRole(recipe: Recipe): "protein" | "carb" | "legume" | "salad" | "prep" | "beverage" | "other" {
  if (recipe.category === "protein") return "protein";
  if (recipe.category === "carb") return "carb";
  if (recipe.category === "legume") return "legume";
  if (recipe.category === "salad") return "salad";
  if (recipe.category === "prep") return "prep";
  if (isLowCalBeverage(recipe)) return "beverage";
  return "other";
}

/**
 * Build a unique signature for an option (sorted base IDs joined with "|").
 * Used to detect duplicate options regardless of scale.
 */
function getOptionSignature(option: string[]): string {
  return option
    .map((id) => safeParseScaledId(id).baseId)
    .sort()
    .join("|");
}

/**
 * Diversify a cloned option by swapping as many items as possible with
 * alternatives from the user's preferences that are NOT already used in
 * blacklisted options. Preserves nutritional roles and structural
 * requirements (lunch/dinner need protein+carb).
 */
function diversifyClonedOption(
  mealKey: MealKey,
  source: string[],
  blacklistedSignatures: Set<string>,
  recipes: Record<MealKey, MealBlock>,
  preferences: Record<MealKey, string[]>,
): string[] {
  if (source.length === 0) return source;

  const mealBlock = recipes[mealKey];
  const preferred = mealBlock?.items.filter((r) => (preferences[mealKey] || []).includes(r.id)) || [];
  if (preferred.length === 0) return varyOptionScales(source, recipes);

  const altsByRole = new Map<string, Recipe[]>();
  for (const r of preferred) {
    const role = getRecipeRole(r);
    if (!altsByRole.has(role)) altsByRole.set(role, []);
    altsByRole.get(role)!.push(r);
  }

  const usedAcrossOthers = new Set<string>();
  for (const sig of blacklistedSignatures) {
    for (const baseId of sig.split("|")) {
      if (baseId) usedAcrossOthers.add(baseId);
    }
  }

  const sourceParsed = source.map((scaledId) => {
    const { baseId, scale, isFree } = safeParseScaledId(scaledId);
    const recipe = findRecipeById(baseId, recipes);
    return { scaledId, baseId, scale, isFree, recipe };
  });

  const workingBaseIds = sourceParsed.map((p) => p.baseId);
  const indices = shuffle(sourceParsed.map((_, i) => i));

  for (const idx of indices) {
    const item = sourceParsed[idx];
    if (!item.recipe) continue;
    if (item.isFree) continue;

    const role = getRecipeRole(item.recipe);
    const candidates = (altsByRole.get(role) || []).filter((r) => {
      if (r.id === workingBaseIds[idx]) return false;
      if (workingBaseIds.includes(r.id)) return false;
      return true;
    });
    if (candidates.length === 0) continue;

    const scored = candidates.map((c) => ({
      recipe: c,
      score: usedAcrossOthers.has(c.id) ? 1 : 0,
    }));
    scored.sort((a, b) => a.score - b.score || Math.random() - 0.5);

    for (const { recipe: alt } of scored) {
      const trial = workingBaseIds.slice();
      trial[idx] = alt.id;
      const trialSig = trial.slice().sort().join("|");
      if (!blacklistedSignatures.has(trialSig)) {
        workingBaseIds[idx] = alt.id;
        break;
      }
    }
  }

  // For structured meals, ensure protein + carb still present
  if (isStructuredMeal(mealKey)) {
    const recipesAfter = workingBaseIds
      .map((id) => findRecipeById(id, recipes))
      .filter(Boolean) as Recipe[];
    const hasProtein = recipesAfter.some((r) => recipeHasProteinSource(r));
    const hasCarb = recipesAfter.some((r) => recipeHasCarbSource(r));
    if (!hasProtein) {
      const origProteinIdx = sourceParsed.findIndex((p) => p.recipe && recipeHasProteinSource(p.recipe));
      if (origProteinIdx >= 0) workingBaseIds[origProteinIdx] = sourceParsed[origProteinIdx].baseId;
    }
    if (!hasCarb) {
      const origCarbIdx = sourceParsed.findIndex((p) => p.recipe && recipeHasCarbSource(p.recipe));
      if (origCarbIdx >= 0) workingBaseIds[origCarbIdx] = sourceParsed[origCarbIdx].baseId;
    }
  }

  const newOption = workingBaseIds.map((baseId, i) => {
    const orig = sourceParsed[i];
    if (baseId === orig.baseId) return orig.scaledId;
    return buildScaledRecipeId(baseId, 1);
  });

  return varyOptionScales(newOption, recipes);
}

/**
 * Collect option signatures from opts excluding a given index.
 */
function collectBlacklistedSignatures(opts: string[][], excludeIdx: number): Set<string> {
  const set = new Set<string>();
  for (let k = 0; k < opts.length; k++) {
    if (k === excludeIdx) continue;
    if (opts[k].length === 0) continue;
    set.add(getOptionSignature(opts[k]));
  }
  return set;
}

// ═══════════════════════════════════════════════════════════
// §3 — PURE UTILITY FUNCTIONS
// ═══════════════════════════════════════════════════════════

function clamp(val: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, val));
}

function shuffle<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function getComboKey(ids: string[]): string {
  return [...ids].sort().join("|");
}

/**
 * Strip `:free` tag from a scaled recipe ID for safe parsing.
 * All ID processing should go through this to avoid lookup failures.
 */
function stripFreeTag(scaledId: string): string {
  return scaledId.replace(/:free$/, "");
}

/**
 * Safe parse that handles `:free` tags transparently.
 */
function safeParseScaledId(scaledId: string): { baseId: string; scale: number; isFree: boolean } {
  const isFree = scaledId.endsWith(":free");
  const cleaned = stripFreeTag(scaledId);
  const parsed = parseScaledRecipeId(cleaned);
  return { ...parsed, isFree };
}

/**
 * Safe recipe lookup that handles `:free` tags and scaled IDs.
 */
function safeResolveRecipe(scaledId: string, recipes: Record<MealKey, MealBlock>): Recipe | null {
  const cleaned = stripFreeTag(scaledId);
  return findRecipeById(cleaned, recipes);
}

function resolveRecipeForBudgetRebalance(
  scaledId: string,
  recipes: Record<MealKey, MealBlock>,
): Recipe | undefined {
  const resolved = safeResolveRecipe(scaledId, recipes);
  if (!resolved) return undefined;

  const { isFree } = safeParseScaledId(scaledId);
  if (isFree || isSaladRecipe(resolved)) {
    return { ...resolved, cal: 0 };
  }

  return resolved;
}

function isStructuredMeal(key: MealKey): boolean {
  return key === "lunch" || key === "dinner";
}

/**
 * Garante que a lista de receitas preferidas para almoço/jantar contenha
 * pelo menos uma fonte de proteína. Se o usuário não selecionou nenhuma
 * proteína, injeta automaticamente a proteína mais leve disponível no bloco
 * para preservar a regra estrutural (proteína + carboidrato obrigatórios).
 */
function ensureStructuredMealHasProtein(
  mealKey: MealKey,
  preferred: Recipe[],
  block: MealBlock,
): Recipe[] {
  if (!isStructuredMeal(mealKey)) return preferred;
  if (preferred.length === 0) return preferred;
  const hasProtein = preferred.some((r) => r.category === "protein" || hasProteinIngredients(r));
  if (hasProtein) return preferred;

  // Coleta proteínas disponíveis no bloco (não selecionadas pelo usuário) e escolhe
  // a de menor caloria que não conflite com itens já selecionados.
  const candidateProteins = block.items
    .filter((r) => r.category === "protein")
    .sort((a, b) => a.cal - b.cal);

  for (const candidate of candidateProteins) {
    const trial = [...preferred, candidate];
    if (!hasDirectIngredientOverlap(trial.map((r) => r.id), trial)) {
      return trial;
    }
  }
  // Fallback: ainda assim injeta a primeira proteína disponível para garantir estrutura
  if (candidateProteins.length > 0) {
    return [...preferred, candidateProteins[0]];
  }
  return preferred;
}

function getRecipeCarbs(r: Recipe): number {
  return r.ingredients.reduce((s, i) => s + i.carbs, 0);
}

function getRecipeFat(r: Recipe): number {
  return r.ingredients.reduce((s, i) => s + i.fat, 0);
}

function getMainIngredientGramsForRecipe(recipe: Recipe): number {
  let totalGrams = 0;
  for (const ing of recipe.ingredients) {
    const g = ing.quantity_grams || extractGrams(ing.amount);
    if (g && g > 0) totalGrams += g;
  }
  return totalGrams || 100;
}

function getProteinFamily(id: string, label: string): string | null {
  const combined = `${id} ${label}`.toLowerCase();
  for (const [family, keywords] of PROTEIN_FAMILIES) {
    if (keywords.some((kw) => combined.includes(kw))) return family;
  }
  return null;
}

function isGrainIngredient(name: string): boolean {
  return GRAIN_KEYWORDS.some((kw) => name.toLowerCase().includes(kw));
}
function isLegumeIngredient(name: string): boolean {
  return LEGUME_KEYWORDS.some((kw) => name.toLowerCase().includes(kw));
}
function isProteinIngredient(name: string): boolean {
  return PROTEIN_KEYWORDS_FLOOR.some((kw) => name.toLowerCase().includes(kw));
}

function isLowCalBeverage(recipe: Recipe): boolean {
  // All coffee recipes are treated as free beverages (fixed 240ml portion)
  if (isCoffeeRecipe(recipe)) return true;
  if (recipe.cal >= LOW_CAL_BEVERAGE_THRESHOLD) return false;
  const ingNames = recipe.ingredients.map((i) => i.name.toLowerCase()).join(" ");
  return BEVERAGE_INGREDIENT_KEYWORDS.some((kw) => ingNames.includes(kw));
}

/**
 * Detects pure coffee beverages (water + coffee only, no milk).
 * Café com leite is NOT considered "free" because milk adds real calories
 * and protein that must count toward the meal budget.
 */
function isCoffeeRecipe(recipe: Recipe): boolean {
  const id = recipe.id.toLowerCase();
  // Explicitly exclude milk-based coffee — its calories must count
  if (id.includes("cafe_leite") || id.includes("café_leite")) return false;
  if (id.includes("cafe_puro")) return true;
  const ingNames = recipe.ingredients.map((i) => i.name.toLowerCase());
  const hasMilk = ingNames.some((n) => n.includes("leite"));
  if (hasMilk) return false;
  return ingNames.some((n) => n.includes("café coado") || n.includes("cafe coado"));
}

// ═══════════════════════════════════════════════════════════
// §4 — FRUIT DETECTION & CONFLICT DETECTION
// ═══════════════════════════════════════════════════════════

const FRUIT_KEYWORDS = [
  "banana", "maçã", "maca", "laranja", "melancia", "mamao", "mamão",
  "melao", "melão", "morango", "abacaxi", "uva", "pêra", "pera",
  "kiwi", "caqui", "bergamota", "manga", "fruta",
];

/** IDs of recipes that inherently contain fruit (combos like vitamina, panqueca_banana, etc.) */
const FRUIT_CONTAINING_IDS = [
  "fruta_pasta_amendoim", "iogurte_fruta", "panqueca_banana_aveia",
  "aveia_banana", "vitamina", "suco_verde_cafe",
];

function isFruitRecipe(id: string, label: string): boolean {
  const baseId = safeParseScaledId(id).baseId;
  const combined = `${baseId} ${label}`.toLowerCase();
  if (FRUIT_CONTAINING_IDS.some((fid) => baseId === fid)) return true;
  return FRUIT_KEYWORDS.some((kw) => combined.includes(kw));
}

function comboHasFruit(ids: string[], allRecipes: Recipe[]): boolean {
  return ids.some((id) => {
    const baseId = safeParseScaledId(id).baseId;
    const r = allRecipes.find((rec) => rec.id === baseId);
    return isFruitRecipe(baseId, r?.label ?? "");
  });
}

function isStandaloneFruitRecipe(recipe: Recipe): boolean {
  if (!isFruitRecipe(recipe.id, recipe.label)) return false;
  if (recipe.ingredients.length !== 1) return false;
  const ingredientName = recipe.ingredients[0]?.name.toLowerCase() ?? "";
  return FRUIT_KEYWORDS.some((kw) => ingredientName.includes(kw));
}

function getSelectedBreakfastFruitRecipes(mealKey: MealKey, preferred: Recipe[]): Recipe[] {
  if (mealKey !== "breakfast") return [];
  return preferred.filter(isStandaloneFruitRecipe);
}

function comboHasRequiredFruit(ids: string[], requiredFruitIds: Set<string>): boolean {
  for (const id of ids) {
    if (requiredFruitIds.has(safeParseScaledId(id).baseId)) return true;
  }
  return false;
}

function countBeverages(ids: string[], allRecipes: Recipe[]): number {
  let count = 0;
  for (const id of ids) {
    const r = allRecipes.find((rec) => rec.id === id);
    const combined = `${id} ${r ? r.label : ""}`.toLowerCase();
    if (BEVERAGE_KEYWORDS.some((kw) => combined.includes(kw))) count++;
  }
  return count;
}

function isHeavyItem(id: string, label: string): boolean {
  const combined = `${id} ${label}`.toLowerCase();
  return HEAVY_KEYWORDS.some((kw) => combined.includes(kw));
}

function countHeavyItems(ids: string[], allRecipes: Recipe[]): number {
  let count = 0;
  for (const id of ids) {
    const r = allRecipes.find((rec) => rec.id === id);
    if (isHeavyItem(id, r?.label ?? "")) count++;
  }
  return count;
}

function hasIngredientOverlap(ids: string[], allRecipes: Recipe[]): boolean {
  const counts: Record<string, number> = {};
  for (const id of ids) {
    const r = allRecipes.find((rec) => rec.id === id);
    if (!r) continue;
    const found = new Set<string>();
    for (const ing of r.ingredients) {
      const lower = ing.name.toLowerCase();
      for (const tracked of TRACKED_INGREDIENTS) {
        if (lower.includes(tracked)) found.add(tracked);
      }
    }
    for (const key of found) {
      counts[key] = (counts[key] || 0) + 1;
    }
  }
  return Object.values(counts).some((c) => c > INGREDIENT_MAX_REPEAT);
}

function getDirectOverlapIngredientKey(name: string, grams?: number, cal?: number): string | null {
  const lower = name.toLowerCase();
  if (IGNORE_OVERLAP_KEYWORDS.some((kw) => lower.includes(kw))) return null;
  if ((grams ?? 0) < 15 && (cal ?? 0) < 20) return null;
  for (const [key, keywords] of DIRECT_OVERLAP_GROUPS) {
    if (keywords.some((kw) => lower.includes(kw))) return key;
  }
  return null;
}

function hasDirectIngredientOverlap(ids: string[], allRecipes: Recipe[]): boolean {
  const seen = new Set<string>();
  for (const id of ids) {
    const recipe = allRecipes.find((rec) => rec.id === id);
    if (!recipe) continue;
    const recipeKeys = new Set<string>();
    for (const ing of recipe.ingredients) {
      const grams = ing.quantity_grams || extractGrams(ing.amount);
      const key = getDirectOverlapIngredientKey(ing.name, grams, ing.cal);
      if (key) recipeKeys.add(key);
    }
    for (const key of recipeKeys) {
      if (seen.has(key)) return true;
      seen.add(key);
    }
  }
  return false;
}

function hasConflict(ids: string[], allRecipes: Recipe[]): boolean {
  const labels = ids.map((id) => {
    const r = allRecipes.find((rec) => rec.id === id);
    return r ? `${r.id} ${r.label}`.toLowerCase() : id.toLowerCase();
  });
  for (const group of EXCLUSION_KEYWORDS) {
    let matchCount = 0;
    for (const label of labels) {
      if (group.some((kw) => label.includes(kw))) matchCount++;
    }
    if (matchCount > 1) return true;
  }
  if (hasIngredientOverlap(ids, allRecipes)) return true;
  if (hasDirectIngredientOverlap(ids, allRecipes)) return true;
  return false;
}

// ═══════════════════════════════════════════════════════════
// §5 — RECIPE CLASSIFICATION HELPERS
// ═══════════════════════════════════════════════════════════

function hasProteinIngredients(recipe: Recipe): boolean {
  return recipe.ingredients.some((i) => {
    const n = i.name.toLowerCase();
    return (
      n.includes("ovo") ||
      n.includes("frango") ||
      n.includes("carne") ||
      n.includes("peixe") ||
      n.includes("tilápia") ||
      n.includes("salmão") ||
      n.includes("sardinha") ||
      n.includes("peru") ||
      n.includes("lombo")
    );
  });
}

function recipeHasProteinSource(recipe: Recipe): boolean {
  return recipe.category === "protein" || hasProteinIngredients(recipe);
}

function recipeHasCarbSource(recipe: Recipe): boolean {
  return recipe.category === "carb" || getRecipeCarbs(recipe) >= 12;
}

// Preps are self-contained dishes (e.g. rap10+carne, crepioca, omelete) that
// already include protein and/or carbs internally — treat them as valid on their own.
function isSelfContainedPrep(recipe: Recipe): boolean {
  return recipe.category === "prep";
}

function isStructuredCandidateValid(candidate: Candidate, allRecipes: Recipe[]): boolean {
  // A self-contained prep alone is a valid structured candidate.
  if (
    candidate.ids.some((id) => {
      const r = allRecipes.find((rec) => rec.id === id);
      return r ? isSelfContainedPrep(r) : false;
    })
  ) {
    return !hasDirectIngredientOverlap(candidate.ids, allRecipes);
  }
  const hasProtein = candidate.ids.some((id) => {
    const r = allRecipes.find((rec) => rec.id === id);
    return r ? recipeHasProteinSource(r) : false;
  });
  const hasCarb = candidate.ids.some((id) => {
    const r = allRecipes.find((rec) => rec.id === id);
    return r ? recipeHasCarbSource(r) : false;
  });
  return hasProtein && hasCarb && !hasDirectIngredientOverlap(candidate.ids, allRecipes);
}

function isSaladRecipe(recipe: Recipe): boolean {
  return recipe.category === "salad";
}

function recipesToCandidate(recipes: Recipe[]): Candidate {
  const nonSalad = recipes.filter((r) => !isSaladRecipe(r));
  return {
    ids: recipes.map((r) => r.id),
    cal: nonSalad.reduce((s, r) => s + r.cal, 0),
    protein: nonSalad.reduce((s, r) => s + r.protein, 0),
    carbs: nonSalad.reduce((s, r) => s + getRecipeCarbs(r), 0),
    fat: nonSalad.reduce((s, r) => s + getRecipeFat(r), 0),
    proteinCount: recipes.filter((r) => r.category === "protein").length,
    carbCount: recipes.filter((r) => r.category === "carb").length,
    legumeCount: recipes.filter((r) => r.category === "legume").length,
    saladCount: recipes.filter((r) => r.category === "salad").length,
    prepCount: recipes.filter((r) => r.category === "prep").length,
  };
}

// ═══════════════════════════════════════════════════════════
// §6 — SCALE LIMITS (centralized, with gram-cap safety)
// ═══════════════════════════════════════════════════════════

/**
 * Centralized effective scale limits with gradient protection by category.
 * ALWAYS respects gram-based maxScale from getScaleLimits() — never exceeds it.
 */
function getEffectiveLimits(
  recipe: Recipe,
  currentScale: number,
  originalMinScale: number,
  originalMaxScale: number,
  opts?: { locked?: boolean },
): { effectiveMin: number; effectiveMax: number } {
  if (opts?.locked) {
    return { effectiveMin: currentScale, effectiveMax: currentScale };
  }

  const lowCal = isLowCalDensityRecipe(recipe);

  // --- effectiveMin: gradient protection ---
  let effectiveMin: number;
  if (recipe.category === "salad" || (lowCal && !recipe.category)) {
    effectiveMin = originalMinScale;
  } else if (hasProteinIngredients(recipe) && !recipe.category) {
    effectiveMin = originalMinScale;
  } else if (recipe.category === "protein") {
    effectiveMin = originalMinScale * 0.7;
  } else if (recipe.category === "carb" || recipe.category === "legume") {
    effectiveMin = originalMinScale * 0.5;
  } else {
    effectiveMin = Math.min(originalMinScale, 0.15);
  }

  // --- effectiveMax: ALWAYS capped by gram-based originalMaxScale ---
  // This prevents absurd portions (e.g., 400g chicken, 500g rice)
  let effectiveMax: number;
  if (recipe.category === "salad" || lowCal) {
    effectiveMax = originalMaxScale;
  } else if (recipe.category === "carb" || recipe.category === "legume") {
    effectiveMax = originalMaxScale;
  } else if (recipe.category === "protein") {
    effectiveMax = originalMaxScale;
  } else {
    // Uncategorized: moderate expansion but still capped
    effectiveMax = Math.min(
      Math.max(originalMaxScale, currentScale * 1.5),
      originalMaxScale * 1.3, // Never exceed 130% of gram-based max
    );
  }

  return { effectiveMin, effectiveMax };
}

// ═══════════════════════════════════════════════════════════
// §7 — COMBINATION GENERATOR
// ═══════════════════════════════════════════════════════════

function getCombinations<T>(arr: T[], k: number): T[][] {
  if (k === 1) return arr.map((a) => [a]);
  if (k > arr.length) return [];
  const result: T[][] = [];
  function helper(start: number, current: T[]) {
    if (result.length >= MAX_COMBINATIONS) return;
    if (current.length === k) {
      result.push([...current]);
      return;
    }
    for (let i = start; i < arr.length && result.length < MAX_COMBINATIONS; i++) {
      current.push(arr[i]);
      helper(i + 1, current);
      current.pop();
    }
  }
  helper(0, []);
  return shuffle(result);
}

// ═══════════════════════════════════════════════════════════
// §8 — CANDIDATE SCORING & SELECTION
// ═══════════════════════════════════════════════════════════

interface ComboScoringContext {
  target: MacroTarget | null;
  budget: number;
  usedCombos: Set<string>;
  isStructured: boolean;
  usedProteins: Set<string>;
  usedCarbs: Set<string>;
  usedSides: Set<string>;
  usedGenericIds: Set<string>;
  usedPreps: Set<string>;
  allRecipes: Recipe[];
  completeSaladUsageCount: number;
  hasOtherSalads: boolean;
  usedProteinFamilies: Set<string>;
  /** Selected standalone breakfast fruits that must appear when applicable */
  requiredFruitIds?: Set<string>;
}

function scoreCandidate(c: Candidate, ctx: ComboScoringContext): number {
  const {
    target,
    budget,
    isStructured,
    usedProteins,
    usedCarbs,
    usedSides,
    allRecipes,
    completeSaladUsageCount,
    hasOtherSalads,
    usedProteinFamilies,
  } = ctx;

  let score: number;
  if (target) {
    const mScore = macroScore(c, target);
    const calRatio = c.cal / budget;
    let calPenalty = Math.abs(c.cal - budget) * 0.5;
    if (calRatio < 0.6 || calRatio > 1.4) calPenalty = Math.abs(c.cal - budget) * 3.0;
    else if (calRatio < 0.7 || calRatio > 1.3) calPenalty = Math.abs(c.cal - budget) * 2.0;
    else if (calRatio < 0.8 || calRatio > 1.2) calPenalty = Math.abs(c.cal - budget) * 1.0;
    score = mScore + calPenalty;
  } else {
    score = Math.abs(c.cal - budget);
  }

  if (c.cal < budget * 0.5) score += 300;

  // Protein-to-carb ratio sanity
  if (isStructured) {
    const totalMacroWeight = c.protein + c.carbs + c.fat;
    if (totalMacroWeight > 0 && c.protein / totalMacroWeight < 0.15) score += 150;
  }

  // Structured meal composition rules
  if (isStructured) {
    // Self-contained preps (e.g. rap10+carne, crepioca) already include
    // protein+carb internally — they don't need to satisfy proteinCount/carbCount.
    const isPrepOnly =
      c.prepCount >= 1 && c.proteinCount === 0 && c.carbCount === 0;

    if (!isPrepOnly) {
      if (c.proteinCount !== 1) score += 120;
      if (c.carbCount < 1 || c.carbCount > 2) score += 500;
      if (c.carbCount === 2) score += 6;
    }

    const hasSaladPrefs = allRecipes.some((r) => r.category === "salad");
    if (hasSaladPrefs && c.saladCount === 0) score += 500;
    const maxSalads = c.legumeCount > 0 ? 2 : 3;
    if (c.saladCount > maxSalads) score += 120;

    const completeSaladCount = c.ids.filter((id) => id.includes("salada_completa") || id === "salada_jt").length;
    if (completeSaladCount > 1) score += 300;
    if (completeSaladCount >= 1 && c.saladCount > 1) score += 200;
    if (completeSaladCount >= 1 && hasOtherSalads && completeSaladUsageCount >= 2) score += 400;
    if (completeSaladCount >= 1 && hasOtherSalads && completeSaladUsageCount >= 1) score += 80;
    if (c.legumeCount > 1) score += 120;
    if (c.saladCount + c.legumeCount > 3) score += 150;

    // Egg-only protein without carb is a bad template
    const hasOnlyEggProtein = c.proteinCount === 1 && c.ids.some((id) => id.includes("ovo"));
    if (hasOnlyEggProtein && c.carbCount === 0) score += 300;

    // Zero protein is unacceptable in structured meals (unless covered by a prep)
    if (c.proteinCount === 0 && c.prepCount === 0) score += 500;
  }

  // Rotation penalties
  for (const id of c.ids) {
    const r = allRecipes.find((rec) => rec.id === id);
    if (!r) continue;
    if (r.category === "protein" && usedProteins.has(id)) score += isStructured ? 200 : 60;
    if (r.category === "carb" && usedCarbs.has(id)) score += 40;
    if (r.category === "legume" && usedSides.has(id)) score += 80;
    if (r.category === "salad" && usedSides.has(id)) score += 50;
    // Self-contained preps (rap10, crepioca, sanduíche, omelete completo…) must
    // rotate across options when the user has more than one available.
    if (r.category === "prep" && ctx.usedPreps.has(id)) {
      const availablePreps = allRecipes.filter((rec) => rec.category === "prep").length;
      score += availablePreps > 1 ? 600 : 80;
    }

    // Penalize uncategorized recipes repeating across options (e.g. suco_verde_omelete)
    if (!r.category && ctx.usedGenericIds.has(id)) score += 150;

    if (r.category === "protein") {
      const family = getProteinFamily(id, r.label);
      if (family && usedProteinFamilies.has(family)) score += 100;
    }
  }

  // Combo size penalty
  score += Math.max(0, c.ids.length - 2) * 8;

  // Multiple heavy items in simple meals
  if (!isStructured) {
    if (countHeavyItems(c.ids, allRecipes) > 1) score += 100;
  }

  return score;
}

function findBestCombo(candidates: Candidate[], ctx: ComboScoringContext): Candidate | null {
  let best: Candidate | null = null;
  let bestScore = Infinity;
  const maxBaseCal = ctx.budget * 2.0;

  for (const c of candidates) {
    if (ctx.isStructured && !isStructuredCandidateValid(c, ctx.allRecipes)) continue;
    const key = getComboKey(c.ids);
    if (ctx.usedCombos.has(key)) continue;
    if (c.cal > maxBaseCal) continue;

    const score = scoreCandidate(c, ctx);
    if (score < bestScore) {
      bestScore = score;
      best = c;
    }
  }

  if (best) {
    ctx.usedCombos.add(getComboKey(best.ids));
  }
  return best;
}

// ═══════════════════════════════════════════════════════════
// §9 — CANDIDATE GENERATORS
// ═══════════════════════════════════════════════════════════

function generateStructuredCandidates(preferred: Recipe[], budget: number): Candidate[] {
  const proteins = shuffle(preferred.filter((r) => r.category === "protein"));
  const carbs = shuffle(preferred.filter((r) => r.category === "carb"));
  const legumes = shuffle(preferred.filter((r) => r.category === "legume"));
  const salads = shuffle(preferred.filter((r) => r.category === "salad"));
  const preps = shuffle(preferred.filter((r) => r.category === "prep"));

  // If we lack proteins or carbs but have self-contained preps, still emit
  // prep-based candidates so the user actually gets variety from their picks.
  if (proteins.length === 0 || carbs.length === 0) {
    if (preps.length === 0) return [];
  }

  const oneCarbCandidates: Candidate[] = [];
  const twoCarbCandidates: Candidate[] = [];
  const prepCandidates: Candidate[] = [];
  const seen = new Set<string>();

  const pushCandidate = (combo: Recipe[], targetList: Candidate[]) => {
    const ids = combo.map((r) => r.id);
    if (hasConflict(ids, preferred)) return;
    const key = getComboKey(ids);
    if (seen.has(key)) return;
    seen.add(key);
    targetList.push(recipesToCandidate(combo));
  };

  const hasSalads = salads.length > 0;
  const hasLegumes = legumes.length > 0;

  const isCompleteSalad = (r: Recipe) => r.id.includes("salada_completa") || r.id === "salada_jt";
  const completeSalads = salads.filter(isCompleteSalad);
  const individualSalads = salads.filter((r) => !isCompleteSalad(r));

  const saladCombos: Recipe[][] = [];
  for (const cs of completeSalads) saladCombos.push([cs]);
  for (const s of individualSalads) saladCombos.push([s]);
  if (individualSalads.length >= 2) {
    for (const pair of getCombinations(individualSalads, 2)) saladCombos.push(pair);
  }
  if (individualSalads.length >= 3) {
    for (const triple of getCombinations(individualSalads, 3)) saladCombos.push(triple);
  }

  const addWithSaladsAndLegumes = (base: Recipe[], targetList: Candidate[]) => {
    if (!hasSalads && !hasLegumes) {
      pushCandidate(base, targetList);
    } else if (hasSalads) {
      for (const saladCombo of saladCombos) {
        pushCandidate([...base, ...saladCombo], targetList);
        if (hasLegumes && saladCombo.length <= 2) {
          for (const legume of legumes) {
            pushCandidate([...base, ...saladCombo, legume], targetList);
          }
        }
      }
    } else {
      for (const legume of legumes) {
        pushCandidate([...base, legume], targetList);
      }
    }
  };

  // Each prep is a self-contained dish — emit as single-item candidate (with optional sides).
  for (const prep of preps) {
    if (hasSalads || hasLegumes) {
      addWithSaladsAndLegumes([prep], prepCandidates);
    } else {
      pushCandidate([prep], prepCandidates);
    }
    // Always include the bare prep too, so we always have at least one candidate per prep.
    pushCandidate([prep], prepCandidates);
  }

  for (const protein of proteins) {
    for (const carb of carbs) {
      addWithSaladsAndLegumes([protein, carb], oneCarbCandidates);
    }
  }

  const carbPairs = getCombinations(carbs, 2);
  for (const protein of proteins) {
    for (const [carbA, carbB] of carbPairs) {
      addWithSaladsAndLegumes([protein, carbA, carbB], twoCarbCandidates);
    }
  }

  const hasGoodOneCarbCoverage = oneCarbCandidates.some((c) => c.cal >= budget * 0.9);
  if (hasGoodOneCarbCoverage || twoCarbCandidates.length === 0) {
    return [...prepCandidates, ...oneCarbCandidates];
  }
  return [...prepCandidates, ...oneCarbCandidates, ...twoCarbCandidates];
}

function generateSimpleCandidates(preferred: Recipe[], _budget: number): Candidate[] {
  const shuffled = shuffle(preferred);
  const candidates: Candidate[] = [];

  for (const item of shuffled) {
    candidates.push(recipesToCandidate([item]));
  }

  for (let size = 2; size <= Math.min(MAX_SIMPLE_ITEMS, shuffled.length); size++) {
    const combos = getCombinations(shuffled, size);
    for (const combo of combos) {
      const ids = combo.map((r) => r.id);
      if (hasConflict(ids, preferred)) continue;
      if (countHeavyItems(ids, preferred) > 1) continue;
      if (countBeverages(ids, preferred) > 1) continue;
      candidates.push(recipesToCandidate(combo));
    }
  }
  return candidates;
}

function buildForcedStructuredCandidate(preferred: Recipe[]): Candidate | null {
  const proteins = shuffle(preferred.filter(recipeHasProteinSource));
  if (proteins.length === 0) return null;

  const carbs = shuffle(preferred.filter(recipeHasCarbSource));
  const salads = shuffle(preferred.filter((r) => r.category === "salad"));
  const selected: Recipe[] = [proteins[0]];

  const carb = carbs.find(
    (r) =>
      r.id !== selected[0].id && !hasDirectIngredientOverlap([...selected.map((item) => item.id), r.id], preferred),
  );
  if (carb) selected.push(carb);

  const salad = salads.find((r) => !hasDirectIngredientOverlap([...selected.map((item) => item.id), r.id], preferred));
  if (salad) selected.push(salad);

  const candidate = recipesToCandidate(selected);
  return isStructuredCandidateValid(candidate, preferred) ? candidate : null;
}

// ═══════════════════════════════════════════════════════════
// §10 — CAPACITY-AWARE MACRO DISTRIBUTION
// ═══════════════════════════════════════════════════════════

function estimateMealCapacity(comboRecipes: Recipe[]): MealCapacity {
  let protein = 0,
    carbs = 0,
    fat = 0,
    cal = 0;
  for (const r of comboRecipes) {
    const { maxScale } = getScaleLimits(r);
    const capScale = Math.min(maxScale, 3) * 0.8;
    protein += r.protein * capScale;
    carbs += getRecipeCarbs(r) * capScale;
    fat += getRecipeFat(r) * capScale;
    cal += r.cal * capScale;
  }
  return { protein, carbs, fat, cal };
}

function distributeWithCapacity(
  daily: MacroTarget,
  activeMeals: MealKey[],
  capacities: Record<string, MealCapacity>,
): Record<MealKey, MacroTarget> {
  const totalPct = activeMeals.reduce((s, k) => s + mealCaloriePercent[k], 0);
  const result = {} as Record<MealKey, MacroTarget>;

  const initial: Record<string, MacroTarget> = {};
  for (const key of activeMeals) {
    const pct = mealCaloriePercent[key] / totalPct;
    initial[key] = {
      protein: daily.protein * pct,
      carbs: daily.carbs * pct,
      fat: daily.fat * pct,
      cal: daily.cal * pct,
    };
  }

  const residual = { protein: 0, carbs: 0, fat: 0 };
  const capped: Record<string, MacroTarget> = {};
  const uncappedMeals: string[] = [];

  for (const key of activeMeals) {
    const cap = capacities[key];
    const ini = initial[key];
    const meal: MacroTarget = { ...ini };
    let wasCapped = false;

    if (cap) {
      for (const macro of ["protein", "carbs", "fat"] as const) {
        if (ini[macro] > cap[macro]) {
          meal[macro] = cap[macro];
          residual[macro] += ini[macro] - cap[macro];
          wasCapped = true;
        }
      }
    }

    capped[key] = meal;
    if (!wasCapped) uncappedMeals.push(key);
  }

  if (uncappedMeals.length > 0) {
    const uncappedTotalPct = uncappedMeals.reduce((s, k) => s + mealCaloriePercent[k as MealKey], 0);
    for (const key of uncappedMeals) {
      const share = mealCaloriePercent[key as MealKey] / uncappedTotalPct;
      const calBudget = daily.cal * (mealCaloriePercent[key as MealKey] / totalPct);
      const maxCal = calBudget * 1.05;

      const currentCal = capped[key].protein * 4 + capped[key].carbs * 4 + capped[key].fat * 9;
      const addedCal = residual.protein * share * 4 + residual.carbs * share * 4 + residual.fat * share * 9;
      const calRatio =
        currentCal + addedCal > maxCal && addedCal > 0 ? Math.max(0, (maxCal - currentCal) / addedCal) : 1;

      capped[key].protein += residual.protein * share * calRatio;
      capped[key].carbs += residual.carbs * share * calRatio;
      capped[key].fat += residual.fat * share * calRatio;
    }
  }

  // Enforce per-meal protein floor
  const mainMeals = new Set<string>(["breakfast", "lunch", "dinner"]);
  const proteinFloors: Record<string, number> = {};
  for (const key of activeMeals) {
    proteinFloors[key] = mainMeals.has(key) ? 20 : 10;
  }

  let proteinSurplus = 0;
  const mealsNeedingProtein: string[] = [];
  for (const key of activeMeals) {
    const floor = proteinFloors[key];
    const cap = capacities[key];
    const currentProt = capped[key].protein;
    if (currentProt < floor && cap && cap.protein >= floor) {
      const deficit = floor - currentProt;
      mealsNeedingProtein.push(key);
      capped[key].protein = floor;
      const calAdded = deficit * 4;
      capped[key].carbs = Math.max(0, capped[key].carbs - calAdded / 4);
      proteinSurplus -= deficit;
    } else if (currentProt > floor + 10) {
      proteinSurplus += currentProt - floor - 5;
    }
  }

  if (proteinSurplus < 0 && mealsNeedingProtein.length > 0) {
    const donorMeals = activeMeals.filter(
      (k) => !mealsNeedingProtein.includes(k) && capped[k].protein > proteinFloors[k] + 5,
    );
    const totalDonorProt = donorMeals.reduce((s, k) => s + (capped[k].protein - proteinFloors[k] - 5), 0);
    if (totalDonorProt > 0) {
      const needed = Math.abs(proteinSurplus);
      for (const k of donorMeals) {
        const available = capped[k].protein - proteinFloors[k] - 5;
        const share = (available / totalDonorProt) * needed;
        const reduction = Math.min(share, available);
        capped[k].protein -= reduction;
        capped[k].carbs += (reduction * 4) / 4;
      }
    }
  }

  for (const key of activeMeals) {
    const cal = Math.round(capped[key].protein * 4 + capped[key].carbs * 4 + capped[key].fat * 9);
    result[key] = {
      protein: Math.round(capped[key].protein),
      carbs: Math.round(capped[key].carbs),
      fat: Math.round(capped[key].fat),
      cal,
    };
  }

  return result;
}

// ═══════════════════════════════════════════════════════════
// §11 — GLOBAL SCALE CALIBRATION (3×3 matrix solver)
// ═══════════════════════════════════════════════════════════

function invert3x3(m: number[][]): number[][] | null {
  const [a, b, c] = m[0];
  const [d, e, f] = m[1];
  const [g, h, i] = m[2];

  const A = e * i - f * h;
  const B = -(d * i - f * g);
  const C = d * h - e * g;
  const D = -(b * i - c * h);
  const E = a * i - c * g;
  const F = -(a * h - b * g);
  const G = b * f - c * e;
  const H = -(a * f - c * d);
  const I = a * e - b * d;

  const det = a * A + b * B + c * C;
  if (Math.abs(det) < 1e-9) return null;

  const invDet = 1 / det;
  return [
    [A * invDet, D * invDet, G * invDet],
    [B * invDet, E * invDet, H * invDet],
    [C * invDet, F * invDet, I * invDet],
  ];
}

function calibrateGlobalScales(
  vars: GlobalScaleVar[],
  target: MacroTarget,
  recipes: Record<MealKey, MealBlock>,
  mealCalBudgets: Record<string, number>,
  lockedMeals: Set<MealKey>,
): void {
  if (vars.length === 0) return;

  const maxMealCalRatio = 1.03;

  for (let iter = 0; iter < 30; iter++) {
    // Compute actual discrete-rounded macros
    const actual = { protein: 0, carbs: 0, fat: 0 };
    for (const v of vars) {
      const scaledId = buildScaledRecipeId(v.recipeId, v.scale);
      const resolved = findRecipeById(scaledId, recipes);
      if (!resolved) {
        actual.protein += v.protein * v.scale;
        actual.carbs += v.carbs * v.scale;
        actual.fat += v.fat * v.scale;
        continue;
      }
      actual.protein += resolved.protein;
      actual.carbs += resolved.ingredients.reduce((s, i) => s + i.carbs, 0);
      actual.fat += resolved.ingredients.reduce((s, i) => s + i.fat, 0);
    }

    const residual = {
      protein: target.protein - actual.protein,
      carbs: target.carbs - actual.carbs,
      fat: target.fat - actual.fat,
    };

    const maxError = Math.max(Math.abs(residual.protein), Math.abs(residual.carbs), Math.abs(residual.fat));
    if (maxError <= 0.3) return;

    // Freeze meals at calorie ceiling or locked
    const mealCals: Record<string, number> = {};
    for (const v of vars) {
      const cal = (v.protein * 4 + v.carbs * 4 + v.fat * 9) * v.scale;
      mealCals[v.mealKey] = (mealCals[v.mealKey] ?? 0) + cal;
    }
    const frozenMeals = new Set<string>(lockedMeals);
    for (const [meal, cal] of Object.entries(mealCals)) {
      const budget = mealCalBudgets[meal] ?? Infinity;
      if (cal >= budget * maxMealCalRatio) frozenMeals.add(meal);
    }

    // Build Gram matrix
    const gram = [
      [0, 0, 0],
      [0, 0, 0],
      [0, 0, 0],
    ];
    let adjustableCount = 0;
    for (const v of vars) {
      if (frozenMeals.has(v.mealKey)) continue;
      const atMin = v.scale <= v.minScale + 0.001;
      const atMax = v.scale >= v.maxScale - 0.001;
      if (atMin && atMax) continue;
      const dot = v.protein * residual.protein + v.carbs * residual.carbs + v.fat * residual.fat;
      if (atMin && dot < 0) continue;
      if (atMax && dot > 0) continue;
      gram[0][0] += v.protein * v.protein;
      gram[0][1] += v.protein * v.carbs;
      gram[0][2] += v.protein * v.fat;
      gram[1][0] += v.carbs * v.protein;
      gram[1][1] += v.carbs * v.carbs;
      gram[1][2] += v.carbs * v.fat;
      gram[2][0] += v.fat * v.protein;
      gram[2][1] += v.fat * v.carbs;
      gram[2][2] += v.fat * v.fat;
      adjustableCount++;
    }

    if (adjustableCount === 0) return;
    const inv = invert3x3(gram);
    if (!inv) return;

    const lambda = {
      protein: inv[0][0] * residual.protein + inv[0][1] * residual.carbs + inv[0][2] * residual.fat,
      carbs: inv[1][0] * residual.protein + inv[1][1] * residual.carbs + inv[1][2] * residual.fat,
      fat: inv[2][0] * residual.protein + inv[2][1] * residual.carbs + inv[2][2] * residual.fat,
    };

    const damping = iter < 15 ? 0.5 : 0.3;
    for (const v of vars) {
      if (frozenMeals.has(v.mealKey)) continue;
      const atMin = v.scale <= v.minScale + 0.001;
      const atMax = v.scale >= v.maxScale - 0.001;
      const dot = v.protein * residual.protein + v.carbs * residual.carbs + v.fat * residual.fat;
      if (atMin && dot < 0) continue;
      if (atMax && dot > 0) continue;

      const delta = v.protein * lambda.protein + v.carbs * lambda.carbs + v.fat * lambda.fat;
      v.scale = clamp(v.scale + delta * damping, v.minScale, v.maxScale);
    }

    // Enforce soft calorie ceiling AND floor
    for (const [meal, budget] of Object.entries(mealCalBudgets)) {
      if (lockedMeals.has(meal as MealKey)) continue;
      const mealVars = vars.filter((v) => v.mealKey === meal);
      if (mealVars.length === 0) continue;

      let mealCal = 0;
      for (const v of mealVars) {
        mealCal += (v.protein * 4 + v.carbs * 4 + v.fat * 9) * v.scale;
      }
      if (mealCal <= 0) continue;

      const maxCal = budget * maxMealCalRatio;
      if (mealCal > maxCal) {
        const ratio = maxCal / mealCal;
        for (const v of mealVars) {
          v.scale = clamp(v.scale * ratio, v.minScale, v.maxScale);
        }
      }

      let mealCalAfter = 0;
      for (const v of mealVars) {
        mealCalAfter += (v.protein * 4 + v.carbs * 4 + v.fat * 9) * v.scale;
      }
      const minCal = budget * 0.97;
      if (mealCalAfter < minCal && mealCalAfter > 0) {
        const ratio = minCal / mealCalAfter;
        for (const v of mealVars) {
          v.scale = clamp(v.scale * ratio, v.minScale, v.maxScale);
        }
      }
    }
  }
}

// ═══════════════════════════════════════════════════════════
// §12 — UNIFIED MEAL SOLVE PIPELINE (eliminates duplication)
// ═══════════════════════════════════════════════════════════

interface MealCombo {
  best: Candidate;
  recipes: Recipe[];
}

interface SolveContext {
  macroTargets: MacroTarget;
  targetCalories: number;
  activeMeals: MealKey[];
  recipes: Record<MealKey, MealBlock>;
  preferences: Record<MealKey, string[]>;
}

/**
 * Unified solve pipeline: takes selected combos for all meals and produces
 * calibrated scaled IDs. Used by both initial generation and regeneration,
 * eliminating the code duplication that was the #2 gap.
 */
function solveMealScales(
  mealCombos: Partial<Record<MealKey, MealCombo>>,
  ctx: SolveContext,
): Record<MealKey, string[]> {
  const { macroTargets, activeMeals, recipes, preferences } = ctx;
  const result: Record<MealKey, string[]> = {} as Record<MealKey, string[]>;

  const mealKeys = Object.keys(mealCombos) as MealKey[];
  if (mealKeys.length === 0) return result;

  // 1. Capacity-aware macro distribution
  const capacities: Record<string, MealCapacity> = {};
  for (const key of mealKeys) {
    capacities[key] = estimateMealCapacity(mealCombos[key]!.recipes);
  }
  const mealMacroTargets = distributeWithCapacity(macroTargets, mealKeys, capacities);

  const mealCalBudgets = getNormalizedMealBudgets(macroTargets.cal, mealKeys);

  // 2. Per-meal solve
  const scalesByMeal: Partial<Record<MealKey, Map<string, number>>> = {};
  const globalVars: GlobalScaleVar[] = [];
  const lockedMeals = new Set<MealKey>();

  for (const key of mealKeys) {
    const combo = mealCombos[key]!;
    const mealTarget = mealMacroTargets[key];

    const hasCarb = combo.recipes.some((r) => r.category === "carb");
    const useStructured = isStructuredMeal(key) && hasCarb;
    const scales = useStructured
      ? solveStructuredMealScales(combo.recipes, mealTarget)
      : solveSimpleMealScales(combo.recipes, mealTarget);

    scalesByMeal[key] = scales;

    // Lock single-preference single-recipe meals
    const shouldLock = combo.recipes.length === 1 && (preferences[key] || []).length === 1;
    const onlyRecipe = shouldLock ? combo.recipes[0] : null;
    const forcedScale = onlyRecipe && onlyRecipe.cal > 0 ? mealCalBudgets[key] / onlyRecipe.cal : 1;

    if (shouldLock) {
      lockedMeals.add(key);
      scales.set(combo.recipes[0].id, forcedScale);
    }

    for (const recipe of combo.recipes) {
      const { minScale, maxScale } = getScaleLimits(recipe);
      const startScale = shouldLock ? forcedScale : (scales.get(recipe.id) ?? 1);

      const { effectiveMin, effectiveMax } = getEffectiveLimits(recipe, startScale, minScale, maxScale, {
        locked: shouldLock,
      });

      globalVars.push({
        mealKey: key,
        recipeId: recipe.id,
        protein: recipe.protein,
        carbs: getRecipeCarbs(recipe),
        fat: getRecipeFat(recipe),
        scale: clamp(startScale, effectiveMin, effectiveMax),
        minScale: effectiveMin,
        maxScale: effectiveMax,
      });
    }
  }

  // 3. Global calibration
  calibrateGlobalScales(globalVars, macroTargets, recipes, mealCalBudgets, lockedMeals);

  // 4. Hard enforcement: clamp each meal to 105% ceiling
  for (const [meal, budget] of Object.entries(mealCalBudgets)) {
    if (lockedMeals.has(meal as MealKey)) continue;
    const mealVars = globalVars.filter((v) => v.mealKey === meal);
    if (mealVars.length === 0) continue;
    let mealCal = 0;
    for (const v of mealVars) {
      mealCal += (v.protein * 4 + v.carbs * 4 + v.fat * 9) * v.scale;
    }
    if (mealCal <= 0) continue;
    const hardCap = budget * 1.05;
    if (mealCal > hardCap) {
      const ratio = hardCap / mealCal;
      for (const v of mealVars) {
        v.scale = Math.max(v.minScale, v.scale * ratio);
      }
    }
  }

  // 5. Daily macro enforcement (max 60 passes)
  for (let pass = 0; pass < 60; pass++) {
    const dailyActual = { protein: 0, carbs: 0, fat: 0 };
    for (const v of globalVars) {
      dailyActual.protein += v.protein * v.scale;
      dailyActual.carbs += v.carbs * v.scale;
      dailyActual.fat += v.fat * v.scale;
    }

    const macroChecks = [
      { key: "protein" as const, actual: dailyActual.protein, target: macroTargets.protein },
      { key: "carbs" as const, actual: dailyActual.carbs, target: macroTargets.carbs },
      { key: "fat" as const, actual: dailyActual.fat, target: macroTargets.fat },
    ];

    let worstKey: "protein" | "carbs" | "fat" | null = null;
    let worstDeviation = 0;
    let needsIncrease = false;
    for (const { key, actual, target } of macroChecks) {
      if (target <= 0) continue;
      const ratio = actual / target;
      if (ratio > 1.02) {
        const dev = ratio - 1.02;
        if (dev > worstDeviation) {
          worstDeviation = dev;
          worstKey = key;
          needsIncrease = false;
        }
      } else if (ratio < 0.98) {
        const dev = 0.98 - ratio;
        if (dev > worstDeviation) {
          worstDeviation = dev;
          worstKey = key;
          needsIncrease = true;
        }
      }
    }

    if (!worstKey) break;

    const check = macroChecks.find((c) => c.key === worstKey)!;
    const diff = needsIncrease ? check.target - check.actual : check.actual - check.target;
    if (diff <= 0) break;

    const targetVars = globalVars.filter((v) => {
      if (lockedMeals.has(v.mealKey)) return false;
      return v[worstKey!] > 0;
    });

    const totalContrib = targetVars.reduce((s, v) => s + v[worstKey!] * v.scale, 0);
    if (totalContrib <= 0) break;

    for (const v of targetVars) {
      const contribution = v[worstKey!] * v.scale;
      const share = contribution / totalContrib;
      const adjustment = diff * share;
      const newContrib = needsIncrease ? contribution + adjustment : Math.max(0, contribution - adjustment);
      const newScale = v[worstKey!] > 0 ? newContrib / v[worstKey!] : v.scale;
      v.scale = clamp(newScale, v.minScale, v.maxScale);
    }
  }

  // 6. Write calibrated scales and apply
  for (const v of globalVars) {
    scalesByMeal[v.mealKey]?.set(v.recipeId, v.scale);
  }

  for (const key of mealKeys) {
    const combo = mealCombos[key]!;
    const scales = scalesByMeal[key] ?? new Map<string, number>();
    result[key] = applyScales(combo.best.ids, scales);
  }

  return result;
}

// ═══════════════════════════════════════════════════════════
// §13 — UNIFIED COMBO SELECTION (eliminates duplication)
// ═══════════════════════════════════════════════════════════

interface ComboSelectionContext {
  activeMeals: MealKey[];
  recipes: Record<MealKey, MealBlock>;
  preferences: Record<MealKey, string[]>;
  targetCalories: number;
  macroTargets?: MacroTarget;
  allOptions: Record<MealKey, string[][]>;
  optionIndex: number;
  optionsCount: number;
  /** Protein families used across meals for cross-meal rotation */
  optionProteinFamilies: Set<string>;
}

/**
 * Select best combo for each meal. Used by both initial generation and regeneration.
 */
function selectCombosForOption(ctx: ComboSelectionContext): {
  mealCombos: Partial<Record<MealKey, MealCombo>>;
  fallbacks: Partial<Record<MealKey, string[]>>;
} {
  const {
    activeMeals,
    recipes,
    preferences,
    targetCalories,
    macroTargets,
    allOptions,
    optionIndex,
    optionsCount,
    optionProteinFamilies,
  } = ctx;
  const totalPct = activeMeals.reduce((s, k) => s + mealCaloriePercent[k], 0);

  const mealCombos: Partial<Record<MealKey, MealCombo>> = {};
  const fallbacks: Partial<Record<MealKey, string[]>> = {};

  // Compute initial meal macro targets
  const initialMealMacros: Partial<Record<MealKey, MacroTarget>> = {};
  if (macroTargets) {
    for (const key of activeMeals) {
      const pct = mealCaloriePercent[key] / totalPct;
      initialMealMacros[key] = {
        protein: Math.round(macroTargets.protein * pct),
        carbs: Math.round(macroTargets.carbs * pct),
        fat: Math.round(macroTargets.fat * pct),
        cal: Math.round(macroTargets.cal * pct),
      };
    }
  }

  for (const mealKey of activeMeals) {
    const block = recipes[mealKey];
    const preferred = ensureStructuredMealHasProtein(
      mealKey,
      block.items.filter((r) => (preferences[mealKey] || []).includes(r.id)),
      block,
    );
    if (preferred.length === 0) {
      fallbacks[mealKey] = [];
      continue;
    }

    const mealTarget = initialMealMacros[mealKey] ?? null;
    const mealBudget = Math.round(targetCalories * (mealCaloriePercent[mealKey] / totalPct));

    // Build usage tracking from other options (not current)
    const usedCombos = new Set<string>();
    const usedProteins = new Set<string>();
    const usedCarbs = new Set<string>();
    const usedSides = new Set<string>();
    const usedGenericIds = new Set<string>();
    const usedPreps = new Set<string>();
    let completeSaladUsageCount = 0;

    for (let oi = 0; oi < optionsCount; oi++) {
      if (oi === optionIndex) continue;
      const prevOpt = allOptions[mealKey][oi];
      if (!prevOpt) continue;
      const normalizedIds = prevOpt.map((id) => safeParseScaledId(id).baseId);
      usedCombos.add(getComboKey(normalizedIds));
      let optHasCompleteSalad = false;
      for (const baseId of normalizedIds) {
        const r = preferred.find((rec) => rec.id === baseId);
        if (r?.category === "protein") usedProteins.add(baseId);
        else if (r?.category === "carb") usedCarbs.add(baseId);
        else if (r?.category === "legume" || r?.category === "salad") usedSides.add(baseId);
        else if (r?.category === "prep") usedPreps.add(baseId);
        else if (r && !r.category) usedGenericIds.add(baseId);
        if (baseId.includes("salada_completa") || baseId === "salada_jt") optHasCompleteSalad = true;
      }
      if (optHasCompleteSalad) completeSaladUsageCount++;
    }

    const individualSaladsAvailable = preferred.some(
      (r) => r.category === "salad" && !r.id.includes("salada_completa") && r.id !== "salada_jt",
    );

    const selectedBreakfastFruits = getSelectedBreakfastFruitRecipes(mealKey, preferred);
    const requiredFruitIds = new Set(selectedBreakfastFruits.map((recipe) => recipe.id));

    const scoringCtx: ComboScoringContext = {
      target: mealTarget,
      budget: mealBudget,
      usedCombos,
      isStructured: isStructuredMeal(mealKey),
      usedProteins,
      usedCarbs,
      usedSides,
      usedGenericIds,
      usedPreps,
      allRecipes: preferred,
      completeSaladUsageCount,
      hasOtherSalads: individualSaladsAvailable,
      usedProteinFamilies: optionProteinFamilies,
      requiredFruitIds,
    };

    let candidates: Candidate[];
    if (isStructuredMeal(mealKey)) {
      candidates = generateStructuredCandidates(preferred, mealBudget);
      if (candidates.length === 0) {
        const simpleCandidates = generateSimpleCandidates(preferred, mealBudget);
        const viable = simpleCandidates.filter((c) => isStructuredCandidateValid(c, preferred));
        candidates = viable.length > 0 ? viable : [];
      }
    } else {
      candidates = generateSimpleCandidates(preferred, mealBudget);
    }

    let best = findBestCombo(candidates, scoringCtx);

    // Relaxed retry: when no unused combo found, try to at least avoid the
    // most recently picked combo so consecutive options stay distinct.
    if (!best) {
      const usedArr = Array.from(scoringCtx.usedCombos);
      const lastUsed = usedArr[usedArr.length - 1];
      const onlyExcludeLast = new Set<string>(lastUsed ? [lastUsed] : []);
      best = findBestCombo(candidates, { ...scoringCtx, usedCombos: onlyExcludeLast });
      if (!best) {
        const relaxedCtx = { ...scoringCtx, usedCombos: new Set<string>() };
        best = findBestCombo(candidates, relaxedCtx);
      }
    }

    // Structured meal validation + fallbacks
    if (isStructuredMeal(mealKey) && (!best || !isStructuredCandidateValid(best, preferred))) {
      const validCandidates = candidates.filter((c) => isStructuredCandidateValid(c, preferred));
      if (validCandidates.length > 0) {
        best =
          findBestCombo(validCandidates, scoringCtx) ??
          findBestCombo(validCandidates, { ...scoringCtx, usedCombos: new Set<string>() });
      }
      if (!best || !isStructuredCandidateValid(best, preferred)) {
        best = buildForcedStructuredCandidate(preferred);
        if (!best) {
          const allSimple = generateSimpleCandidates(preferred, mealBudget);
          best = findBestCombo(allSimple, { ...scoringCtx, usedCombos: new Set<string>(), isStructured: false });
        }
      }
    }

    if (best) {
      const comboRecipes = best.ids.map((id) => preferred.find((r) => r.id === id)).filter(Boolean) as Recipe[];
      mealCombos[mealKey] = { best, recipes: comboRecipes };

      // Track protein families for cross-meal rotation
      for (const r of comboRecipes) {
        if (r.category === "protein") {
          const family = getProteinFamily(r.id, r.label);
          if (family) optionProteinFamilies.add(family);
        }
      }
    } else {
      // Last resort: closest calorie match
      const sorted = [...preferred].sort((a, b) => Math.abs(a.cal - mealBudget) - Math.abs(b.cal - mealBudget));
      fallbacks[mealKey] = sorted.length > 0 ? [sorted[0].id] : [];
    }
  }

  return { mealCombos, fallbacks };
}

// ═══════════════════════════════════════════════════════════
// §14 — OPTION STATE MANAGEMENT
// ═══════════════════════════════════════════════════════════

function saveOptionState(allOptions: Record<MealKey, string[][]>, oIdx: number): OptionState {
  const meals = {} as Record<MealKey, string[]>;
  for (const key of mealOrder) {
    meals[key] = [...(allOptions[key][oIdx] || [])];
  }
  return { meals };
}

function restoreOptionState(allOptions: Record<MealKey, string[][]>, oIdx: number, state: OptionState): void {
  for (const key of mealOrder) {
    allOptions[key][oIdx] = [...(state.meals[key] || [])];
  }
}

// ═══════════════════════════════════════════════════════════
// §15 — DISCRETE MACRO MEASUREMENT
// ═══════════════════════════════════════════════════════════

function computeDiscreteDailyMacros(
  allOptions: Record<MealKey, string[][]>,
  activeMeals: MealKey[],
  oIdx: number,
  recipes: Record<MealKey, MealBlock>,
): { protein: number; carbs: number; fat: number } {
  const totals = { protein: 0, carbs: 0, fat: 0 };
  for (const key of activeMeals) {
    const mealIds = allOptions[key][oIdx] || [];
    for (const scaledId of mealIds) {
      const { isFree } = safeParseScaledId(scaledId);
      if (isFree) continue;
      const resolved = safeResolveRecipe(scaledId, recipes);
      if (!resolved) continue;
      // Salads are "à vontade" — excluded from calorie/macro budgets
      if (isSaladRecipe(resolved)) continue;
      totals.protein += resolved.protein;
      totals.carbs += resolved.ingredients.reduce((s, i) => s + i.carbs, 0);
      totals.fat += resolved.ingredients.reduce((s, i) => s + i.fat, 0);
    }
  }
  return totals;
}

function computeDiscreteDailyCalories(
  allOptions: Record<MealKey, string[][]>,
  activeMeals: MealKey[],
  oIdx: number,
  recipes: Record<MealKey, MealBlock>,
): number {
  let totalCal = 0;
  for (const key of activeMeals) {
    const mealIds = allOptions[key][oIdx] || [];
    for (const scaledId of mealIds) {
      const { isFree } = safeParseScaledId(scaledId);
      if (isFree) continue;
      const resolved = safeResolveRecipe(scaledId, recipes);
      // Salads are "à vontade" — excluded from calorie budgets
      if (resolved && !isSaladRecipe(resolved)) totalCal += resolved.cal;
    }
  }
  return totalCal;
}

/**
 * Compute total macro deviation score — derived from macros only.
 * Calories are NOT checked separately because they're derived from P*4+C*4+F*9.
 * This fixes the double-check issue (gap #10).
 */
function macroDeviationScore(
  totals: { protein: number; carbs: number; fat: number },
  macroTargets: MacroTarget,
): number {
  let score = 0;
  for (const macro of ["protein", "carbs", "fat"] as const) {
    if (macroTargets[macro] <= 0) continue;
    score += Math.abs(totals[macro] / macroTargets[macro] - 1.0);
  }
  return score;
}

function isWithinMacroWindow(
  totals: { protein: number; carbs: number; fat: number },
  macroTargets: MacroTarget,
  lo: number,
  hi: number,
): boolean {
  for (const macro of ["protein", "carbs", "fat"] as const) {
    if (macroTargets[macro] <= 0) continue;
    const ratio = totals[macro] / macroTargets[macro];
    if (ratio < lo || ratio > hi) return false;
  }
  return true;
}

// ═══════════════════════════════════════════════════════════
// §16 — POST-GENERATION NUDGE (discrete scale adjustment)
// ═══════════════════════════════════════════════════════════

function nudgeDailyMacros(
  allOptions: Record<MealKey, string[][]>,
  activeMeals: MealKey[],
  oIdx: number,
  macroTargets: MacroTarget,
  recipes: Record<MealKey, MealBlock>,
  preferences: Record<MealKey, string[]>,
  lo = 0.98,
  hi = 1.02,
): boolean {
  const initialState = saveOptionState(allOptions, oIdx);
  const initialTotals = computeDiscreteDailyMacros(allOptions, activeMeals, oIdx, recipes);
  const initialScore = macroDeviationScore(initialTotals, macroTargets);
  const mealBudgets = getNormalizedMealBudgets(macroTargets.cal, activeMeals);

  for (let attempt = 0; attempt < 15; attempt++) {
    const dailyTotals = computeDiscreteDailyMacros(allOptions, activeMeals, oIdx, recipes);
    if (isWithinMacroWindow(dailyTotals, macroTargets, lo, hi)) return true;

    // Find worst macro
    let worstKey: "protein" | "carbs" | "fat" | null = null;
    let worstDev = 0;
    let needsIncrease = false;
    for (const macro of ["protein", "carbs", "fat"] as const) {
      if (macroTargets[macro] <= 0) continue;
      const ratio = dailyTotals[macro] / macroTargets[macro];
      if (ratio > hi) {
        const d = ratio - hi;
        if (d > worstDev) {
          worstDev = d;
          worstKey = macro;
          needsIncrease = false;
        }
      } else if (ratio < lo) {
        const d = lo - ratio;
        if (d > worstDev) {
          worstDev = d;
          worstKey = macro;
          needsIncrease = true;
        }
      }
    }
    if (!worstKey) return true;

    const diff = needsIncrease
      ? macroTargets[worstKey] - dailyTotals[worstKey]
      : dailyTotals[worstKey] - macroTargets[worstKey];
    if (diff <= 0.1) return true;

    const damping = attempt < 8 ? 0.7 : 0.5;
    const dampedDiff = diff * damping;

    interface NudgeItem {
      mealKey: MealKey;
      idx: number;
      baseId: string;
      currentScale: number;
      macroContrib: number;
    }
    const collectItems = (respectMealBudgetBias: boolean): NudgeItem[] => {
      const items: NudgeItem[] = [];
      for (const key of activeMeals) {
        const mealIds = allOptions[key][oIdx] || [];
        if (mealIds.length === 1 && (preferences[key] || []).length === 1) continue;

        let currentMealCalories = 0;
        for (const scaledId of mealIds) {
          const { isFree } = safeParseScaledId(scaledId);
          if (isFree) continue;
          const resolved = safeResolveRecipe(scaledId, recipes);
          if (resolved && !isSaladRecipe(resolved)) currentMealCalories += resolved.cal;
        }

        const mealBudget = mealBudgets[key] || 0;
        if (respectMealBudgetBias && mealBudget > 0) {
          if (needsIncrease && currentMealCalories >= mealBudget * 1.03) continue;
          if (!needsIncrease && currentMealCalories <= mealBudget * 0.97) continue;
        }

        for (let i = 0; i < mealIds.length; i++) {
          const { baseId, scale: s, isFree } = safeParseScaledId(mealIds[i]);
          if (isFree) continue;
          const base = findRecipeById(baseId, recipes);
          if (!base || isSaladRecipe(base)) continue;
          const macroVal =
            worstKey === "protein" ? base.protein : worstKey === "carbs" ? getRecipeCarbs(base) : getRecipeFat(base);
          if (macroVal <= 0) continue;
          items.push({ mealKey: key, idx: i, baseId, currentScale: s, macroContrib: macroVal });
        }
      }
      return items;
    };

    let items = collectItems(true);
    if (items.length === 0) items = collectItems(false);

    if (items.length === 0) break;
    items.sort((a, b) => b.macroContrib * b.currentScale - a.macroContrib * a.currentScale);

    const totalContrib = items.reduce((s, it) => s + it.macroContrib * it.currentScale, 0);
    if (totalContrib <= 0) break;

    let anyChanged = false;
    for (const item of items) {
      const contrib = item.macroContrib * item.currentScale;
      const share = contrib / totalContrib;
      const adj = dampedDiff * share;

      const baseRecipe = findRecipeById(item.baseId, recipes)!;
      const { minScale, maxScale } = getScaleLimits(baseRecipe);
      let newScale: number;
      if (needsIncrease) {
        newScale = item.currentScale + adj / item.macroContrib;
      } else {
        newScale = item.currentScale - adj / item.macroContrib;
      }
      const { effectiveMin, effectiveMax } = getEffectiveLimits(baseRecipe, item.currentScale, minScale, maxScale);
      newScale = clamp(newScale, effectiveMin, effectiveMax);

      if (Math.abs(newScale - item.currentScale) > 0.001) {
        anyChanged = true;
        const mealIds = allOptions[item.mealKey][oIdx];
        const { isFree } = safeParseScaledId(mealIds[item.idx]);
        mealIds[item.idx] = buildScaledRecipeId(item.baseId, newScale) + (isFree ? ":free" : "");
      }
    }

    if (!anyChanged) break;

    // Clamp meals that nudging inflated beyond budget — prevents 4x overshoots
    for (const key of activeMeals) {
      const mealIds = allOptions[key][oIdx] || [];
      if (mealIds.length === 0) continue;
      if (mealIds.length === 1 && (preferences[key] || []).length === 1) continue;
      let mealCal = 0;
      for (const sid of mealIds) {
        const { isFree: free } = safeParseScaledId(sid);
        if (free) continue;
        const r = safeResolveRecipe(sid, recipes);
        if (r && !isSaladRecipe(r)) mealCal += r.cal;
      }
      const budget = mealBudgets[key] || 0;
      if (budget > 0 && mealCal > budget * 1.10) {
        const correction = (budget * 1.05) / mealCal;
        for (let ii = 0; ii < mealIds.length; ii++) {
          const { baseId: bId, scale: sc, isFree: fr } = safeParseScaledId(mealIds[ii]);
          if (fr) continue;
          const base = findRecipeById(bId, recipes);
          if (!base || isSaladRecipe(base)) continue;
          const { minScale: mn, maxScale: mx } = getScaleLimits(base);
          const { effectiveMin: eMin } = getEffectiveLimits(base, sc, mn, mx);
          mealIds[ii] = buildScaledRecipeId(bId, Math.max(eMin, sc * correction));
        }
      }
    }
  }

  // Verify improvement
  const finalTotals = computeDiscreteDailyMacros(allOptions, activeMeals, oIdx, recipes);
  const finalScore = macroDeviationScore(finalTotals, macroTargets);
  if (finalScore > initialScore + 0.001) {
    restoreOptionState(allOptions, oIdx, initialState);
    return isWithinMacroWindow(initialTotals, macroTargets, lo, hi);
  }

  return isWithinMacroWindow(finalTotals, macroTargets, lo, hi);
}

// ═══════════════════════════════════════════════════════════
// §17 — PER-MEAL CALORIE NUDGE
// ═══════════════════════════════════════════════════════════

function nudgeMealCalories(
  allOptions: Record<MealKey, string[][]>,
  activeMeals: MealKey[],
  oIdx: number,
  macroTargets: MacroTarget,
  recipes: Record<MealKey, MealBlock>,
  preferences: Record<MealKey, string[]>,
): void {
  const totalPct = activeMeals.reduce((s, k) => s + mealCaloriePercent[k], 0);

  for (const mealKey of activeMeals) {
    const budget = macroTargets.cal * (mealCaloriePercent[mealKey] / totalPct);
    if (!budget || budget <= 0) continue;
    const mealIds = allOptions[mealKey][oIdx] || [];
    if (mealIds.length === 0) continue;
    if (mealIds.length === 1 && (preferences[mealKey] || []).length === 1) continue;

    let bestIds: string[] = [...mealIds];
    let bestDeviation = Infinity;

    // Compute initial deviation (exclude :free items and salads — consistent with computeDiscreteDailyCalories)
    {
      let initCal = 0;
      for (const sid of mealIds) {
        const { isFree } = safeParseScaledId(sid);
        if (isFree) continue;
        const r = safeResolveRecipe(sid, recipes);
        if (r && !isSaladRecipe(r)) initCal += r.cal;
      }
      bestDeviation = Math.abs(initCal - budget);
    }

    for (let attempt = 0; attempt < 25; attempt++) {
      const currentIds = allOptions[mealKey][oIdx] || [];
      let discreteCal = 0;
      for (const scaledId of currentIds) {
        const { isFree } = safeParseScaledId(scaledId);
        if (isFree) continue;
        const resolved = safeResolveRecipe(scaledId, recipes);
        if (resolved && !isSaladRecipe(resolved)) discreteCal += resolved.cal;
      }
      if (discreteCal <= 0) break;

      const deviation = Math.abs(discreteCal - budget);
      if (deviation < bestDeviation) {
        bestDeviation = deviation;
        bestIds = [...currentIds];
      }

      const ratio = discreteCal / budget;
      if (ratio >= 0.96 && ratio <= 1.04) break;

      // Try per-item targeted adjustment
      let bestCandidate: string[] | null = null;
      let bestCandidateDev = deviation;

      for (let itemIdx = 0; itemIdx < currentIds.length; itemIdx++) {
        const { baseId, scale: currentScale, isFree } = safeParseScaledId(currentIds[itemIdx]);
        if (isFree) continue;
        const base = findRecipeById(baseId, recipes);
        if (!base || base.cal <= 0) continue;

        const { minScale, maxScale } = getScaleLimits(base);
        const { effectiveMin, effectiveMax } = getEffectiveLimits(base, currentScale, minScale, maxScale);

        const calDiff = budget - discreteCal;
        const baseCal = base.cal;
        if (baseCal <= 0) continue;
        const targetScale = currentScale + calDiff / baseCal;
        const newScale = clamp(targetScale, effectiveMin, effectiveMax);

        const candidateIds = currentIds.map((id, idx) => {
          if (idx === itemIdx) return buildScaledRecipeId(baseId, newScale) + (isFree ? ":free" : "");
          return id;
        });

        let candidateCal = 0;
        for (const sid of candidateIds) {
          const r = safeResolveRecipe(sid, recipes);
          if (r) candidateCal += r.cal;
        }
        const candidateDev = Math.abs(candidateCal - budget);
        if (candidateDev < bestCandidateDev) {
          bestCandidateDev = candidateDev;
          bestCandidate = candidateIds;
        }
      }

      // Uniform scaling fallback
      {
        const correction = budget / discreteCal;
        const uniformIds: string[] = [];
        for (const scaledId of currentIds) {
          const { baseId, scale: currentScale, isFree } = safeParseScaledId(scaledId);
          if (isFree) {
            uniformIds.push(scaledId);
            continue;
          }
          const base = findRecipeById(baseId, recipes);
          if (!base) {
            uniformIds.push(scaledId);
            continue;
          }
          const { minScale, maxScale } = getScaleLimits(base);
          const { effectiveMin, effectiveMax } = getEffectiveLimits(base, currentScale, minScale, maxScale);
          const newScale = clamp(currentScale * correction, effectiveMin, effectiveMax);
          uniformIds.push(buildScaledRecipeId(baseId, newScale));
        }
        let uniformCal = 0;
        for (const sid of uniformIds) {
          const r = safeResolveRecipe(sid, recipes);
          if (r) uniformCal += r.cal;
        }
        const uniformDev = Math.abs(uniformCal - budget);
        if (uniformDev < bestCandidateDev) {
          bestCandidateDev = uniformDev;
          bestCandidate = uniformIds;
        }
      }

      if (bestCandidate) {
        allOptions[mealKey][oIdx] = bestCandidate;
      } else {
        break;
      }
    }

    // Restore best state if current is worse
    const finalIds = allOptions[mealKey][oIdx] || [];
    let finalCal = 0;
    for (const sid of finalIds) {
      const { isFree } = safeParseScaledId(sid);
      if (isFree) continue;
      const r = safeResolveRecipe(sid, recipes);
      if (r && !isSaladRecipe(r)) finalCal += r.cal;
    }
    if (Math.abs(finalCal - budget) > bestDeviation) {
      allOptions[mealKey][oIdx] = bestIds;
    }
  }
}

// ═══════════════════════════════════════════════════════════
// §18 — STRUCTURAL VALIDATORS (portion floors, cultural rules)
// ═══════════════════════════════════════════════════════════

function enforcePortionFloors(
  allOptions: Record<MealKey, string[][]>,
  activeMeals: MealKey[],
  oIdx: number,
  macroTargets: MacroTarget,
  recipes: Record<MealKey, MealBlock>,
): void {
  const totalPct = activeMeals.reduce((s, k) => s + mealCaloriePercent[k], 0);
  const mainMealKeys: MealKey[] = ["lunch", "dinner"];

  for (const mealKey of mainMealKeys) {
    if (!activeMeals.includes(mealKey)) continue;
    const mealIds = allOptions[mealKey][oIdx] || [];
    if (mealIds.length === 0) continue;

    const mealBudget = macroTargets.cal * (mealCaloriePercent[mealKey] / totalPct);
    const idsToRemove: number[] = [];

    for (let i = 0; i < mealIds.length; i++) {
      const { baseId, scale: currentScale, isFree } = safeParseScaledId(mealIds[i]);
      if (isFree) continue;
      const base = findRecipeById(baseId, recipes);
      if (!base) continue;

      let requiredFloorGrams = 0;
      let mainIngGrams = 0;
      let floorCategory = "";

      for (const ing of base.ingredients) {
        const g = ing.quantity_grams || extractGrams(ing.amount);
        if (!g) continue;
        if (isGrainIngredient(ing.name) && g > mainIngGrams) {
          requiredFloorGrams = 60;
          mainIngGrams = g;
          floorCategory = "grain";
        } else if (isLegumeIngredient(ing.name) && g > mainIngGrams) {
          requiredFloorGrams = 60;
          mainIngGrams = g;
          floorCategory = "legume";
        } else if (isProteinIngredient(ing.name) && g > mainIngGrams) {
          requiredFloorGrams = 80;
          mainIngGrams = g;
          floorCategory = "protein";
        }
      }

      if (requiredFloorGrams <= 0 || mainIngGrams <= 0) continue;
      const scaledGrams = mainIngGrams * currentScale;
      if (scaledGrams >= requiredFloorGrams) continue;

      const neededScale = requiredFloorGrams / mainIngGrams;

      // Check if bumping respects gram-based maxScale
      const { maxScale } = getScaleLimits(base);
      const safeNeededScale = Math.min(neededScale, maxScale);

      // Check calorie impact
      let mealCal = 0;
      for (const sid of mealIds) {
        const r = safeResolveRecipe(sid, recipes);
        if (r) mealCal += r.cal;
      }
      const currentItemCal = safeResolveRecipe(mealIds[i], recipes)?.cal || 0;
      const newItemCal = (base.cal / currentScale) * safeNeededScale;
      const newMealCal = mealCal - currentItemCal + newItemCal;

      if (newMealCal > mealBudget * 1.08) {
        // STRUCTURAL RULE: lunch/dinner must keep protein + carb + legume.
        // Never remove these — instead, shrink other items (or accept slight overflow)
        // to make room for the floor of the current item.
        const isStructural =
          base.category === "protein" || base.category === "carb" || base.category === "legume";

        if (isStructural) {
          // Shrink the largest non-structural item (or the largest protein if this is a carb/legume)
          // to free up calories so we can apply the floor on this item.
          const overflow = newMealCal - mealBudget * 1.08;
          let donorIdx = -1;
          let donorPriority = -1;
          for (let j = 0; j < mealIds.length; j++) {
            if (j === i) continue;
            const { baseId: bId } = safeParseScaledId(mealIds[j]);
            const bBase = findRecipeById(bId, recipes);
            if (!bBase || bBase.category === "salad") continue;
            // Prefer shrinking protein (priority 3 > carb 1 > legume 1 > other 2)
            const pri =
              bBase.category === "protein" ? 3 : bBase.category === "carb" || bBase.category === "legume" ? 1 : 2;
            if (pri > donorPriority) {
              donorPriority = pri;
              donorIdx = j;
            }
          }

          if (donorIdx >= 0) {
            const { baseId: dId, scale: dScale } = safeParseScaledId(mealIds[donorIdx]);
            const dBase = findRecipeById(dId, recipes);
            if (dBase && dBase.cal > 0) {
              const reduceScale = Math.min(dScale * 0.5, overflow / dBase.cal);
              const newDScale = Math.max(0.5, dScale - reduceScale);
              mealIds[donorIdx] = buildScaledRecipeId(dId, newDScale);
            }
          }
          // Apply the floor regardless — keeping structure is more important than ±8% overflow
          mealIds[i] = buildScaledRecipeId(baseId, safeNeededScale);
        } else {
          // Non-structural item: safe to remove and redistribute
          idsToRemove.push(i);
          for (let j = 0; j < mealIds.length; j++) {
            if (j === i || idsToRemove.includes(j)) continue;
            const { baseId: bId, scale: bScale } = safeParseScaledId(mealIds[j]);
            const bBase = findRecipeById(bId, recipes);
            if (bBase && (bBase.category === "protein" || bBase.category === "carb")) {
              if (bBase.cal > 0) {
                const addScale = currentItemCal / bBase.cal;
                const { maxScale: bMax } = getScaleLimits(bBase);
                const newBScale = Math.min(bScale + addScale, bMax);
                mealIds[j] = buildScaledRecipeId(bId, newBScale);
              }
              break;
            }
          }
        }
      } else {
        mealIds[i] = buildScaledRecipeId(baseId, safeNeededScale);
      }
    }

    for (let ri = idsToRemove.length - 1; ri >= 0; ri--) {
      mealIds.splice(idsToRemove[ri], 1);
    }
    allOptions[mealKey][oIdx] = mealIds;
  }
}

function enforcePresentationFloors(
  allOptions: Record<MealKey, string[][]>,
  activeMeals: MealKey[],
  oIdx: number,
  recipes: Record<MealKey, MealBlock>,
): void {
  for (const mealKey of activeMeals) {
    const mealIds = allOptions[mealKey][oIdx] || [];
    if (mealIds.length === 0) continue;

    const idsToRemove: number[] = [];
    for (let i = 0; i < mealIds.length; i++) {
      const { baseId, scale: currentScale, isFree } = safeParseScaledId(mealIds[i]);
      if (isFree) continue;
      const base = findRecipeById(baseId, recipes);
      if (!base) continue;

      // Whole-recipe floor for uncategorized
      if (!base.category) {
        const limits = getRecipeGramLimits(base);
        const baseGrams = getMainIngredientGramsForRecipe(base);
        const scaledGrams = baseGrams * currentScale;
        if (scaledGrams < limits.minGrams) {
          idsToRemove.push(i);
          continue;
        }
      }

      // Individual ingredient floors
      let needsBump = false;
      let maxNeededScale = currentScale;
      for (const ing of base.ingredients) {
        const ingName = ing.name.toLowerCase();
        const ingGrams = ing.quantity_grams || extractGrams(ing.amount);
        if (!ingGrams || ingGrams <= 0) continue;

        for (const [keyword, floor] of INGREDIENT_FLOORS) {
          if (!ingName.includes(keyword)) continue;
          const scaledIngGrams = ingGrams * currentScale;
          if (scaledIngGrams < floor) {
            const neededScale = floor / ingGrams;
            if (neededScale > maxNeededScale) {
              maxNeededScale = neededScale;
              needsBump = true;
            }
          }
        }
      }

      if (needsBump) {
        const { maxScale } = getScaleLimits(base);
        const clampedScale = Math.min(maxNeededScale, maxScale);
        if (clampedScale > currentScale) {
          mealIds[i] = buildScaledRecipeId(baseId, clampedScale);
        }
      }
    }

    for (let ri = idsToRemove.length - 1; ri >= 0; ri--) {
      mealIds.splice(idsToRemove[ri], 1);
    }
    allOptions[mealKey][oIdx] = mealIds;
  }
}

/**
 * Enforce arroz + feijão ≥ 150g combined.
 * FIX: clamps boost to maxScale to prevent absurd portions (gap #8).
 */
function enforceArrozFeijao(
  allOptions: Record<MealKey, string[][]>,
  activeMeals: MealKey[],
  oIdx: number,
  recipes: Record<MealKey, MealBlock>,
): void {
  const mainMealKeys: MealKey[] = ["lunch", "dinner"];
  for (const mealKey of mainMealKeys) {
    if (!activeMeals.includes(mealKey)) continue;
    const mealIds = allOptions[mealKey][oIdx] || [];

    let arrozIdx = -1,
      feijaoIdx = -1;
    let arrozGrams = 0,
      feijaoGrams = 0;

    for (let i = 0; i < mealIds.length; i++) {
      const { baseId, scale } = safeParseScaledId(mealIds[i]);
      const base = findRecipeById(baseId, recipes);
      if (!base) continue;
      for (const ing of base.ingredients) {
        const g = ing.quantity_grams || extractGrams(ing.amount);
        if (!g) continue;
        const lower = ing.name.toLowerCase();
        if (lower.includes("arroz") && arrozIdx === -1) {
          arrozIdx = i;
          arrozGrams = g * scale;
        }
        if ((lower.includes("feijão") || lower.includes("lentilha")) && feijaoIdx === -1) {
          feijaoIdx = i;
          feijaoGrams = g * scale;
        }
      }
    }

    if (arrozIdx === -1 || feijaoIdx === -1) continue;
    const combined = arrozGrams + feijaoGrams;
    if (combined >= 150) continue;

    const boostFactor = 150 / combined;

    // Apply boost with maxScale clamping (FIX for gap #8)
    const boostItem = (idx: number) => {
      const { baseId, scale } = safeParseScaledId(mealIds[idx]);
      const base = findRecipeById(baseId, recipes);
      const { maxScale } = base ? getScaleLimits(base) : { maxScale: scale * boostFactor };
      const newScale = Math.min(scale * boostFactor, maxScale);
      mealIds[idx] = buildScaledRecipeId(baseId, newScale);
    };

    boostItem(arrozIdx);
    boostItem(feijaoIdx);
  }
}

function enforceMinProteinInMainMeals(
  allOptions: Record<MealKey, string[][]>,
  activeMeals: MealKey[],
  oIdx: number,
  recipes: Record<MealKey, MealBlock>,
): void {
  const mainMealKeys: MealKey[] = ["lunch", "dinner"];
  for (const mealKey of mainMealKeys) {
    if (!activeMeals.includes(mealKey)) continue;
    const mealIds = allOptions[mealKey][oIdx] || [];
    if (mealIds.length === 0) continue;

    let hasCategoryProtein = false;
    for (const scaledId of mealIds) {
      const { baseId } = safeParseScaledId(scaledId);
      const base = findRecipeById(baseId, recipes);
      if (base?.category === "protein") hasCategoryProtein = true;
    }

    if (!hasCategoryProtein) {
      for (let i = 0; i < mealIds.length; i++) {
        const { baseId, scale } = safeParseScaledId(mealIds[i]);
        const base = findRecipeById(baseId, recipes);
        if (!base) continue;
        if (base.category === "salad") {
          const { maxScale } = getScaleLimits(base);
          if (scale > maxScale) {
            mealIds[i] = buildScaledRecipeId(baseId, maxScale);
          }
        }
      }
    }
  }
}

/**
 * Force-include low-cal beverages (café, chá) from user preferences if missing from the option.
 * Skips structured meals (lunch/dinner). Adds at most one beverage per meal.
 * If a beverage already exists in the option, does nothing.
 */
function forceIncludeBeverages(
  allOptions: Record<MealKey, string[][]>,
  activeMeals: MealKey[],
  oIdx: number,
  recipes: Record<MealKey, MealBlock>,
  preferences: Record<MealKey, string[]>,
): void {
  for (const key of activeMeals) {
    if (key === "lunch" || key === "dinner") continue;
    const mealIds = allOptions[key][oIdx];
    if (!mealIds) continue;

    // Check if any beverage already exists in the option
    const alreadyHasBeverage = mealIds.some(sid => {
      const { baseId } = safeParseScaledId(sid);
      const r = findRecipeById(baseId, recipes);
      return r && isLowCalBeverage(r);
    });
    if (alreadyHasBeverage) continue;

    const prefs = preferences[key] || [];
    const mealRecipes = recipes[key]?.items || [];
    const currentBaseIds = mealIds.map(sid => safeParseScaledId(sid).baseId);
    for (const prefId of prefs) {
      const prefRecipe = findRecipeById(prefId, recipes);
      if (prefRecipe && isLowCalBeverage(prefRecipe)) {
        if (!hasConflict([...currentBaseIds, prefId], mealRecipes)) {
          mealIds.push(prefId + ":free");
          break;
        }
      }
    }
  }
}

function tagFreeBeverages(
  allOptions: Record<MealKey, string[][]>,
  activeMeals: MealKey[],
  oIdx: number,
  recipes: Record<MealKey, MealBlock>,
): void {
  for (const mealKey of activeMeals) {
    const mealIds = allOptions[mealKey][oIdx] || [];
    for (let i = 0; i < mealIds.length; i++) {
      if (mealIds[i].includes(":free")) continue;
      const { baseId } = safeParseScaledId(mealIds[i]);
      const base = findRecipeById(baseId, recipes);
      if (base && isLowCalBeverage(base)) {
        // Always use baseId:free (no scale) — one cup, fixed portion
        mealIds[i] = baseId + ":free";
      }
    }
  }
}

// ═══════════════════════════════════════════════════════════
// §19 — CALORIC DISTRIBUTION FLOORS
// ═══════════════════════════════════════════════════════════

const CALORIC_FLOORS: Partial<Record<MealKey, number>> = {
  breakfast: 0.18,
  lunch: 0.28,
  dinner: 0.23,
};

function checkCaloricFloors(
  allOptions: Record<MealKey, string[][]>,
  activeMeals: MealKey[],
  oIdx: number,
  targetCalories: number,
  recipes: Record<MealKey, MealBlock>,
): boolean {
  for (const [mealKey, minPct] of Object.entries(CALORIC_FLOORS) as [MealKey, number][]) {
    if (!activeMeals.includes(mealKey)) continue;
    const mealIds = allOptions[mealKey][oIdx] || [];
    let mealCal = 0;
    for (const sid of mealIds) {
      const r = safeResolveRecipe(sid, recipes);
      if (r) mealCal += r.cal;
    }
    if (mealCal < targetCalories * minPct * 0.95) return false;
  }
  return true;
}

function boostUnderperformingMeals(
  allOptions: Record<MealKey, string[][]>,
  activeMeals: MealKey[],
  oIdx: number,
  targetCalories: number,
  recipes: Record<MealKey, MealBlock>,
): void {
  const normalizedBudgets = getNormalizedMealBudgets(targetCalories, activeMeals);

  for (const [mealKey, minPct] of Object.entries(CALORIC_FLOORS) as [MealKey, number][]) {
    if (!activeMeals.includes(mealKey)) continue;
    const mealIds = allOptions[mealKey][oIdx] || [];
    if (mealIds.length === 0) continue;

    let mealCal = 0;
    for (const sid of mealIds) {
      const r = safeResolveRecipe(sid, recipes);
      if (r) mealCal += r.cal;
    }

    const minCal = targetCalories * minPct;
    if (mealCal >= minCal * 0.95 || mealCal <= 0) continue;

    // Cap boost at the normalized budget to prevent overshooting
    const normalizedBudget = normalizedBudgets[mealKey as MealKey] || minCal;
    const targetCal = Math.min(minCal, normalizedBudget);
    const boostRatio = targetCal / mealCal;

    for (let i = 0; i < mealIds.length; i++) {
      const { baseId, scale: currentScale, isFree } = safeParseScaledId(mealIds[i]);
      if (isFree) continue;
      const base = findRecipeById(baseId, recipes);
      if (!base) continue;
      const { maxScale } = getScaleLimits(base);
      const newScale = Math.min(currentScale * boostRatio, maxScale);
      mealIds[i] = buildScaledRecipeId(baseId, newScale);
    }
  }
}

// ═══════════════════════════════════════════════════════════
// §20 — FEASIBILITY SCORING
// ═══════════════════════════════════════════════════════════

function computeFeasibilityScore(
  allOptions: Record<MealKey, string[][]>,
  activeMeals: MealKey[],
  oIdx: number,
  targetCalories: number,
  macroTargets: MacroTarget,
  recipes: Record<MealKey, MealBlock>,
): number {
  let score = 100;
  const mainMealKeys: MealKey[] = ["lunch", "dinner"];

  // 1. Portion floor violations
  for (const mealKey of mainMealKeys) {
    if (!activeMeals.includes(mealKey)) continue;
    const mealIds = allOptions[mealKey][oIdx] || [];
    for (const scaledId of mealIds) {
      const resolved = safeResolveRecipe(scaledId, recipes);
      if (!resolved) continue;
      for (const ing of resolved.ingredients) {
        const g = ing.quantity_grams || extractGrams(ing.amount);
        if (!g) continue;
        if (isGrainIngredient(ing.name) && g < 60) score -= 10;
        if (isLegumeIngredient(ing.name) && g < 60) score -= 10;
        if (isProteinIngredient(ing.name) && g < 80) score -= 10;
      }
    }
  }

  // 2. Calorie distribution
  for (const [mealKey, minPct] of Object.entries(CALORIC_FLOORS) as [MealKey, number][]) {
    if (!activeMeals.includes(mealKey)) continue;
    const mealIds = allOptions[mealKey][oIdx] || [];
    let mealCal = 0;
    for (const sid of mealIds) {
      const r = safeResolveRecipe(sid, recipes);
      if (r) mealCal += r.cal;
    }
    if (mealCal < targetCalories * minPct * 0.95) score -= 15;
  }

  // 3. Main meal structure (protein, carb, solid count)
  for (const mealKey of mainMealKeys) {
    if (!activeMeals.includes(mealKey)) continue;
    const mealIds = allOptions[mealKey][oIdx] || [];

    let solidCount = 0;
    let hasProtein = false;
    let hasCarb = false;
    let totalProtein = 0;

    for (const scaledId of mealIds) {
      const { baseId } = safeParseScaledId(scaledId);
      const base = findRecipeById(baseId, recipes);
      if (!base) continue;
      if (!isLowCalBeverage(base)) solidCount++;
      if (base.category === "protein") hasProtein = true;
      if (base.category === "carb") hasCarb = true;
      const resolved = safeResolveRecipe(scaledId, recipes);
      if (resolved) totalProtein += resolved.protein;
    }

    if (solidCount === 0) score -= 35;
    else if (solidCount === 1) score -= 25;
    if (!hasProtein) score -= 30;
    if (!hasCarb) score -= 30;
    if (totalProtein < 15) score -= 20;
  }

  // 4. Protein family repetition across meals
  const proteinFamiliesUsed: string[] = [];
  for (const mealKey of activeMeals) {
    const mealIds = allOptions[mealKey][oIdx] || [];
    for (const scaledId of mealIds) {
      const { baseId } = safeParseScaledId(scaledId);
      const base = findRecipeById(baseId, recipes);
      if (!base) continue;
      const family = getProteinFamily(base.id, base.label);
      if (family) proteinFamiliesUsed.push(family);
    }
  }
  const familyCounts: Record<string, number> = {};
  for (const f of proteinFamiliesUsed) familyCounts[f] = (familyCounts[f] || 0) + 1;
  for (const count of Object.values(familyCounts)) {
    if (count > 1) score -= 5 * (count - 1);
  }

  // 5. Arroz+feijão < 150g
  for (const mealKey of mainMealKeys) {
    if (!activeMeals.includes(mealKey)) continue;
    const mealIds = allOptions[mealKey][oIdx] || [];
    let arrozG = 0,
      feijaoG = 0;
    let hasArroz = false,
      hasFeijao = false;
    for (const scaledId of mealIds) {
      const resolved = safeResolveRecipe(scaledId, recipes);
      if (!resolved) continue;
      for (const ing of resolved.ingredients) {
        const g = ing.quantity_grams || extractGrams(ing.amount);
        if (!g) continue;
        const lower = ing.name.toLowerCase();
        if (lower.includes("arroz")) {
          arrozG += g;
          hasArroz = true;
        }
        if (lower.includes("feijão") || lower.includes("lentilha")) {
          feijaoG += g;
          hasFeijao = true;
        }
      }
    }
    if (hasArroz && hasFeijao && arrozG + feijaoG < 150) score -= 15;
  }

  // 6. Daily macro accuracy
  const dailyTotals = computeDiscreteDailyMacros(allOptions, activeMeals, oIdx, recipes);
  for (const macro of ["protein", "carbs", "fat"] as const) {
    if (macroTargets[macro] <= 0) continue;
    const ratio = dailyTotals[macro] / macroTargets[macro];
    if (ratio < 0.9 || ratio > 1.1) score -= 25;
    else if (ratio < 0.95 || ratio > 1.05) score -= 15;
  }

  return Math.max(0, score);
}

// ═══════════════════════════════════════════════════════════
// §21 — REBALANCE MEAL TO BUDGET (post-dedup)
// ═══════════════════════════════════════════════════════════

function rebalanceMealToBudget(
  mealKey: MealKey,
  mealIds: string[],
  mealBudget: number,
  recipes: Record<MealKey, MealBlock>,
  preferences: Record<MealKey, string[]>,
): string[] {
  if (mealIds.length === 0 || mealBudget <= 0) return mealIds;
  return rebalanceMealOptionToTarget({
    optionIds: mealIds,
    targetCalories: mealBudget,
    resolveRecipe: (id) => resolveRecipeForBudgetRebalance(id, recipes),
  });
}

function fitOptionToBudget(
  mealKey: MealKey,
  mealIds: string[],
  mealBudget: number,
  recipes: Record<MealKey, MealBlock>,
  preferences: Record<MealKey, string[]>,
): string[] {
  if (mealIds.length === 0 || mealBudget <= 0) return mealIds;

  if (mealIds.length === 1 && (preferences[mealKey] || []).length === 1) {
    const { baseId, isFree } = safeParseScaledId(mealIds[0]);
    const base = findRecipeById(baseId, recipes);
    if (!base) return mealIds;
    if (isFree || isLowCalBeverage(base)) return [`${baseId}:free`];
  }

  return rebalanceMealToBudget(mealKey, mealIds, mealBudget, recipes, preferences);
}

function ensureBreakfastFruitPresence(
  allOptions: Record<MealKey, string[][]>,
  mealBudgets: Record<MealKey, number>,
  recipes: Record<MealKey, MealBlock>,
  preferences: Record<MealKey, string[]>,
): void {
  const preferredBreakfast = recipes.breakfast.items.filter((r) => (preferences.breakfast || []).includes(r.id));
  const selectedFruitRecipes = getSelectedBreakfastFruitRecipes("breakfast", preferredBreakfast);
  if (selectedFruitRecipes.length === 0) return;

  const breakfastBudget = mealBudgets.breakfast ?? 0;

  const computeMealCalories = (ids: string[]) =>
    ids.reduce((sum, scaledId) => {
      const { isFree } = safeParseScaledId(scaledId);
      if (isFree) return sum;
      const resolved = safeResolveRecipe(scaledId, recipes);
      return sum + ((resolved && !isSaladRecipe(resolved)) ? resolved.cal : 0);
    }, 0);

  for (let optIdx = 0; optIdx < allOptions.breakfast.length; optIdx++) {
    const mealIds = allOptions.breakfast[optIdx] || [];
    if (mealIds.length === 0) {
      allOptions.breakfast[optIdx] = [selectedFruitRecipes[0].id];
      continue;
    }
    if (comboHasRequiredFruit(mealIds, new Set(selectedFruitRecipes.map((recipe) => recipe.id)))) continue;

    const currentBaseIds = new Set(mealIds.map((id) => safeParseScaledId(id).baseId));
    const currentCalories = computeMealCalories(mealIds);
    const fruitToAdd =
      selectedFruitRecipes
        .filter((recipe) => !currentBaseIds.has(recipe.id))
        .sort(
          (a, b) =>
            Math.abs(currentCalories + a.cal - breakfastBudget) - Math.abs(currentCalories + b.cal - breakfastBudget),
        )[0] ?? selectedFruitRecipes[0];

    allOptions.breakfast[optIdx] = fitOptionToBudget(
      "breakfast",
      [...mealIds, fruitToAdd.id],
      breakfastBudget > 0 ? breakfastBudget : currentCalories + fruitToAdd.cal,
      recipes,
      preferences,
    );
  }
}

/**
 * Simple calorie guard: uniformly scales all items to hit daily calorie target.
 * More stable than macro-specific nudging because it preserves per-meal calorie ratios.
 */
function calorieGuard(
  allOptions: Record<MealKey, string[][]>,
  activeMeals: MealKey[],
  oIdx: number,
  targetCalories: number,
  recipes: Record<MealKey, MealBlock>,
  preferences: Record<MealKey, string[]>,
  tolerance = 0.01,
): void {
  for (let iter = 0; iter < 8; iter++) {
    const totalCal = computeDiscreteDailyCalories(allOptions, activeMeals, oIdx, recipes);
    if (totalCal <= 0) return;
    const ratio = totalCal / targetCalories;
    if (ratio >= 1 - tolerance && ratio <= 1 + tolerance) return;

    const correction = targetCalories / totalCal;
    for (const mealKey of activeMeals) {
      const mealIds = allOptions[mealKey][oIdx] || [];
      if (mealIds.length === 1 && (preferences[mealKey] || []).length === 1) continue;
      for (let i = 0; i < mealIds.length; i++) {
        const { baseId, scale, isFree } = safeParseScaledId(mealIds[i]);
        if (isFree) continue;
        const base = findRecipeById(baseId, recipes);
        if (!base || isSaladRecipe(base)) continue;
        const { minScale, maxScale } = getScaleLimits(base);
        const { effectiveMin, effectiveMax } = getEffectiveLimits(base, scale, minScale, maxScale);
        mealIds[i] = buildScaledRecipeId(baseId, clamp(scale * correction, effectiveMin, effectiveMax));
      }
    }
  }
}

// ═══════════════════════════════════════════════════════════
// §22 — UNIFIED REGENERATION (replaces duplicated logic)
// ═══════════════════════════════════════════════════════════

function regenerateOption(
  allOptions: Record<MealKey, string[][]>,
  oIdx: number,
  ctx: SolveContext & { optionsCount: number },
): void {
  const { macroTargets, targetCalories, activeMeals, recipes, preferences, optionsCount } = ctx;

  const savedState = saveOptionState(allOptions, oIdx);
  const savedTotals = computeDiscreteDailyMacros(allOptions, activeMeals, oIdx, recipes);
  const savedScore = macroDeviationScore(savedTotals, macroTargets);

  const optionProteinFamilies = new Set<string>();

  const { mealCombos, fallbacks } = selectCombosForOption({
    activeMeals,
    recipes,
    preferences,
    targetCalories,
    macroTargets,
    allOptions,
    optionIndex: oIdx,
    optionsCount,
    optionProteinFamilies,
  });

  const mealKeys = Object.keys(mealCombos) as MealKey[];
  if (mealKeys.length === 0) return;

  // Use unified solve pipeline (eliminates duplication — gap #2)
  const solvedMeals = solveMealScales(mealCombos, {
    macroTargets,
    targetCalories,
    activeMeals,
    recipes,
    preferences,
  });

  // Write results
  for (const key of mealOrder) {
    allOptions[key][oIdx] = solvedMeals[key] || fallbacks[key] || allOptions[key][oIdx] || [];
  }

  // Force-include low-cal beverages (café) from preferences if missing
  forceIncludeBeverages(allOptions, activeMeals, oIdx, recipes, preferences);

  // Compare with saved state
  const newTotals = computeDiscreteDailyMacros(allOptions, activeMeals, oIdx, recipes);
  const newScore = macroDeviationScore(newTotals, macroTargets);
  if (newScore > savedScore) {
    restoreOptionState(allOptions, oIdx, savedState);
  }
}

// ═══════════════════════════════════════════════════════════
// §23 — POST-GENERATION VERIFICATION PIPELINE
// ═══════════════════════════════════════════════════════════

/**
 * Unified post-generation verification.
 * Runs a monotonic pipeline: each phase can only improve the score.
 * Phases are ordered to minimize mutual interference (gap #1).
 */
function verifyAndRefineOption(
  allOptions: Record<MealKey, string[][]>,
  oIdx: number,
  macroTargets: MacroTarget,
  targetCalories: number,
  activeMeals: MealKey[],
  recipes: Record<MealKey, MealBlock>,
  preferences: Record<MealKey, string[]>,
  optionsCount: number,
): void {
  const solveCtx: SolveContext & { optionsCount: number } = {
    macroTargets,
    targetCalories,
    activeMeals,
    recipes,
    preferences,
    optionsCount,
  };

  // Phase 1: Per-meal calorie enforcement
  nudgeMealCalories(allOptions, activeMeals, oIdx, macroTargets, recipes, preferences);

  // Phase 2: Daily macro convergence (98-102%)
  if (!nudgeDailyMacros(allOptions, activeMeals, oIdx, macroTargets, recipes, preferences)) {
    for (let regen = 0; regen < 3; regen++) {
      regenerateOption(allOptions, oIdx, solveCtx);
      nudgeMealCalories(allOptions, activeMeals, oIdx, macroTargets, recipes, preferences);
      if (nudgeDailyMacros(allOptions, activeMeals, oIdx, macroTargets, recipes, preferences)) break;
    }
  }

  // Phase 3: Re-enforce per-meal calories (macro nudge may have shifted them)
  nudgeMealCalories(allOptions, activeMeals, oIdx, macroTargets, recipes, preferences);

  // Phase 4: Structural validators (portion floors, cultural rules)
  // These run AFTER calorie convergence because they may add small calorie deviations
  // that we'll fix in Phase 5.
  enforceMinProteinInMainMeals(allOptions, activeMeals, oIdx, recipes);
  enforceArrozFeijao(allOptions, activeMeals, oIdx, recipes);

  if (!checkCaloricFloors(allOptions, activeMeals, oIdx, targetCalories, recipes)) {
    boostUnderperformingMeals(allOptions, activeMeals, oIdx, targetCalories, recipes);
  }

  enforcePortionFloors(allOptions, activeMeals, oIdx, macroTargets, recipes);
  enforcePresentationFloors(allOptions, activeMeals, oIdx, recipes);

  // Phase 4.5: Hard cap — clamp any meal that blew past budget after structural validators
  const postStructBudgets = getNormalizedMealBudgets(targetCalories, activeMeals);
  clampMealsToBudgetCeiling(allOptions, activeMeals, oIdx, postStructBudgets, recipes, preferences, 1.05);

  // Phase 5: Final convergence after structural adjustments
  nudgeMealCalories(allOptions, activeMeals, oIdx, macroTargets, recipes, preferences);
  for (let w = 0; w < 3; w++) {
    const lo = [0.99, 0.97, 0.95][w];
    const hi = [1.01, 1.03, 1.05][w];
    if (nudgeDailyMacros(allOptions, activeMeals, oIdx, macroTargets, recipes, preferences, lo, hi)) break;
  }

  // Phase 6: Tag beverages
  tagFreeBeverages(allOptions, activeMeals, oIdx, recipes);

  // Phase 7: Feasibility check — regenerate if poor
  const score = computeFeasibilityScore(allOptions, activeMeals, oIdx, targetCalories, macroTargets, recipes);
  const totalCalories = computeDiscreteDailyCalories(allOptions, activeMeals, oIdx, recipes);
  const calorieRatio = targetCalories > 0 ? totalCalories / targetCalories : 1;

  if (score < 70 || calorieRatio < 0.97 || calorieRatio > 1.03) {
    const savedState = saveOptionState(allOptions, oIdx);
    const savedScore = score;

    for (let attempt = 0; attempt < 3; attempt++) {
      regenerateOption(allOptions, oIdx, solveCtx);

      // Run abbreviated verification on regenerated option
      nudgeMealCalories(allOptions, activeMeals, oIdx, macroTargets, recipes, preferences);
      nudgeDailyMacros(allOptions, activeMeals, oIdx, macroTargets, recipes, preferences);
      nudgeMealCalories(allOptions, activeMeals, oIdx, macroTargets, recipes, preferences);
      enforceMinProteinInMainMeals(allOptions, activeMeals, oIdx, recipes);
      enforceArrozFeijao(allOptions, activeMeals, oIdx, recipes);
      if (!checkCaloricFloors(allOptions, activeMeals, oIdx, targetCalories, recipes)) {
        boostUnderperformingMeals(allOptions, activeMeals, oIdx, targetCalories, recipes);
      }
      enforcePortionFloors(allOptions, activeMeals, oIdx, macroTargets, recipes);
      enforcePresentationFloors(allOptions, activeMeals, oIdx, recipes);
      // Hard cap after structural validators in regen loop
      const regenBudgets = getNormalizedMealBudgets(targetCalories, activeMeals);
      clampMealsToBudgetCeiling(allOptions, activeMeals, oIdx, regenBudgets, recipes, preferences, 1.05);
      nudgeMealCalories(allOptions, activeMeals, oIdx, macroTargets, recipes, preferences);
      for (let w = 0; w < 3; w++) {
        const lo = [0.99, 0.97, 0.95][w];
        const hi = [1.01, 1.03, 1.05][w];
        if (nudgeDailyMacros(allOptions, activeMeals, oIdx, macroTargets, recipes, preferences, lo, hi)) break;
      }
      tagFreeBeverages(allOptions, activeMeals, oIdx, recipes);

      const newScore = computeFeasibilityScore(allOptions, activeMeals, oIdx, targetCalories, macroTargets, recipes);
      if (newScore >= 70) break;
      if (newScore < savedScore) {
        restoreOptionState(allOptions, oIdx, savedState);
      }
    }
  }
}

// ═══════════════════════════════════════════════════════════
// §24 — DEDUPLICATION
// ═══════════════════════════════════════════════════════════

function deduplicateOptions(
  allOptions: Record<MealKey, string[][]>,
  activeMeals: MealKey[],
  recipes: Record<MealKey, MealBlock>,
  preferences: Record<MealKey, string[]>,
  macroTargets: MacroTarget | undefined,
  targetCalories: number,
): void {
  const totalPct = activeMeals.reduce((s, k) => s + mealCaloriePercent[k], 0);

  for (const mealKey of activeMeals) {
    const opts = allOptions[mealKey];
    if (opts.length <= 1) continue;

    const indicesToRemove = new Set<number>();

    for (let i = 1; i < opts.length; i++) {
      if (indicesToRemove.has(i)) continue;
      const baseIdsI = opts[i]
        .map((id) => safeParseScaledId(id).baseId)
        .sort()
        .join(",");
      const baseSetI = new Set(opts[i].map((id) => safeParseScaledId(id).baseId));

      for (let j = 0; j < i; j++) {
        if (indicesToRemove.has(j)) continue;
        const baseIdsJ = opts[j]
          .map((id) => safeParseScaledId(id).baseId)
          .sort()
          .join(",");

        const isSingleItemDup =
          opts[i].length === 1 &&
          opts[j].length === 1 &&
          safeParseScaledId(opts[i][0]).baseId === safeParseScaledId(opts[j][0]).baseId;

        if (baseIdsI === baseIdsJ || isSingleItemDup) {
          // Collect blacklisted combos
          const blacklistedCombos = new Set<string>();
          const blacklistedBaseIds = new Set<string>();
          for (let k = 0; k < opts.length; k++) {
            if (k === i || indicesToRemove.has(k)) continue;
            blacklistedCombos.add(
              opts[k]
                .map((id) => safeParseScaledId(id).baseId)
                .sort()
                .join(","),
            );
            for (const id of opts[k]) blacklistedBaseIds.add(safeParseScaledId(id).baseId);
          }

          const preferredRaw = recipes[mealKey]?.items.filter((r) => preferences[mealKey]?.includes(r.id)) || [];
          const preferred = recipes[mealKey]
            ? ensureStructuredMealHasProtein(mealKey, preferredRaw, recipes[mealKey])
            : preferredRaw;
          const dedupPct = mealCaloriePercent[mealKey] / totalPct;
          const mealBudget = (macroTargets?.cal || targetCalories) * dedupPct;
          const mealTarget: MacroTarget = macroTargets
            ? {
                protein: Math.round(macroTargets.protein * dedupPct),
                carbs: Math.round(macroTargets.carbs * dedupPct),
                fat: Math.round(macroTargets.fat * dedupPct),
                cal: Math.round(macroTargets.cal * dedupPct),
              }
            : { protein: 0, carbs: 0, fat: 0, cal: mealBudget };

          let candidates = isStructuredMeal(mealKey)
            ? generateStructuredCandidates(preferred, mealBudget)
            : generateSimpleCandidates(preferred, mealBudget);
          if (candidates.length === 0 && isStructuredMeal(mealKey)) {
            // Fallback: usa combos simples mas filtra para garantir presença de proteína
            const simple = generateSimpleCandidates(preferred, mealBudget);
            candidates = simple.filter((c) => c.proteinCount >= 1);
            if (candidates.length === 0) candidates = simple;
          }

          const nonDup = candidates.filter((c) => {
            const sortedIds = c.ids.slice().sort().join(",");
            if (blacklistedCombos.has(sortedIds)) return false;
            if (!isStructuredMeal(mealKey) && c.ids.length === 1 && blacklistedBaseIds.has(c.ids[0])) return false;
            return true;
          });

          let replaced = false;
          if (nonDup.length > 0) {
            const regenBreakfastHasFruit = mealKey === "breakfast" &&
              getSelectedBreakfastFruitRecipes(mealKey, preferred).length > 0;
            const requiredFruitIds = regenBreakfastHasFruit
              ? new Set(getSelectedBreakfastFruitRecipes(mealKey, preferred).map((recipe) => recipe.id))
              : undefined;
            const scoringCtx: ComboScoringContext = {
              target: mealTarget,
              budget: mealBudget,
              usedCombos: new Set<string>(),
              isStructured: isStructuredMeal(mealKey),
              usedProteins: new Set(),
              usedCarbs: new Set(),
              usedSides: new Set(),
              usedGenericIds: new Set(),
              usedPreps: new Set(),
              allRecipes: preferred,
              completeSaladUsageCount: 0,
              hasOtherSalads: false,
              usedProteinFamilies: new Set(),
              requiredFruitIds,
            };

            let newBest = findBestCombo(nonDup, scoringCtx);

            // For structured meals, force a combo with protein when available
            if (isStructuredMeal(mealKey)) {
              const hasProteinInCombo = (c: Candidate) =>
                c.ids.some((id) => {
                  const r = preferred.find((rec) => rec.id === id);
                  return r ? recipeHasProteinSource(r) : false;
                });
              if (!newBest || !hasProteinInCombo(newBest)) {
                const withProt = nonDup.filter(hasProteinInCombo);
                if (withProt.length > 0) {
                  const protBest = findBestCombo(withProt, { ...scoringCtx, usedCombos: new Set() });
                  if (protBest) newBest = protBest;
                }
              }
            }

            if (newBest) {
              const comboRecipes = newBest.ids
                .map((id) => preferred.find((r) => r.id === id))
                .filter(Boolean) as Recipe[];
              const hasCarb = comboRecipes.some((r) => r.category === "carb");
              const useStructured = isStructuredMeal(mealKey) && hasCarb;
              const scales = useStructured
                ? solveStructuredMealScales(comboRecipes, mealTarget)
                : solveSimpleMealScales(comboRecipes, mealTarget);
              opts[i] = rebalanceMealToBudget(
                mealKey,
                applyScales(newBest.ids, scales),
                mealBudget,
                recipes,
                preferences,
              );
              replaced = true;
            }
          }

          if (!replaced) {
            // Keep the duplicate — do NOT remove. Pass 7 guarantees optionsCount.
          }
          break;
        }
      }
    }

    if (indicesToRemove.size > 0) {
      const sorted = Array.from(indicesToRemove).sort((a, b) => b - a);
      for (const idx of sorted) {
        opts.splice(idx, 1);
      }
    }
  }
}

// ═══════════════════════════════════════════════════════════
// §25 — MAIN GENERATOR
// ═══════════════════════════════════════════════════════════

export function generateDiet(input: GeneratorInput): MealSelection {
  const { preferences, targetCalories, macroTargets, optionsCount, recipes } = input;
  const result: MealSelection = {
    breakfast: [[]],
    morning_snack: [[]],
    lunch: [[]],
    snack: [[]],
    dinner: [[]],
    optional: [[]],
  };

  const activeMeals = mealOrder.filter((key) => (preferences[key] || []).length > 0);
  if (activeMeals.length === 0) return result;

  const allOptions: Record<MealKey, string[][]> = {
    breakfast: [],
    morning_snack: [],
    lunch: [],
    snack: [],
    dinner: [],
    optional: [],
  };

  // ─── PASS 1: Generate each option ───
  for (let optIdx = 0; optIdx < optionsCount; optIdx++) {
    const optionProteinFamilies = new Set<string>();

    const { mealCombos, fallbacks } = selectCombosForOption({
      activeMeals,
      recipes,
      preferences,
      targetCalories,
      macroTargets,
      allOptions,
      optionIndex: optIdx,
      optionsCount,
      optionProteinFamilies,
    });

    if (macroTargets) {
      // Use unified solve pipeline
      const solvedMeals = solveMealScales(mealCombos, {
        macroTargets,
        targetCalories,
        activeMeals,
        recipes,
        preferences,
      });

      for (const key of mealOrder) {
        allOptions[key].push(solvedMeals[key] || fallbacks[key] || []);
      }
    } else {
      // No macro targets — calorie-based scaling
      const totalPct = activeMeals.reduce((s, k) => s + mealCaloriePercent[k], 0);
      for (const key of mealOrder) {
        const combo = mealCombos[key];
        if (combo) {
          const mealBudget = Math.round(targetCalories * mealCaloriePercent[key]);
          const scale = combo.best.cal > 0 ? mealBudget / combo.best.cal : 1;
          allOptions[key].push(combo.best.ids.map((id) => buildScaledRecipeId(id, scale)));
        } else {
          allOptions[key].push(fallbacks[key] || []);
        }
      }
    }

    // Force-include beverages (café) from preferences after combo selection
    forceIncludeBeverages(allOptions, activeMeals, optIdx, recipes, preferences);
  }

  // ─── PASS 2: Post-generation verification ───
  if (macroTargets) {
    for (let optIdx = 0; optIdx < optionsCount; optIdx++) {
      verifyAndRefineOption(
        allOptions,
        optIdx,
        macroTargets,
        targetCalories,
        activeMeals,
        recipes,
        preferences,
        optionsCount,
      );
    }

    // Final convergence pass
    for (let optIdx = 0; optIdx < optionsCount; optIdx++) {
      nudgeMealCalories(allOptions, activeMeals, optIdx, macroTargets, recipes, preferences);
      for (let attempt = 0; attempt < 4; attempt++) {
        const totals = computeDiscreteDailyMacros(allOptions, activeMeals, optIdx, recipes);
        if (isWithinMacroWindow(totals, macroTargets, 0.99, 1.01)) break;
        if (!nudgeDailyMacros(allOptions, activeMeals, optIdx, macroTargets, recipes, preferences, 0.99, 1.01)) {
          regenerateOption(allOptions, optIdx, {
            macroTargets,
            targetCalories,
            activeMeals,
            recipes,
            preferences,
            optionsCount,
          });
          nudgeMealCalories(allOptions, activeMeals, optIdx, macroTargets, recipes, preferences);
        }
      }
      tagFreeBeverages(allOptions, activeMeals, optIdx, recipes);
    }
  }

  // ─── PASS 3: Deduplication ───
  deduplicateOptions(allOptions, activeMeals, recipes, preferences, macroTargets, targetCalories);

  // ─── PASS 3.5: Re-include beverages after dedup may have rebuilt options ───
  for (let optIdx = 0; optIdx < optionsCount && optIdx < (allOptions[activeMeals[0]]?.length || 0); optIdx++) {
    forceIncludeBeverages(allOptions, activeMeals, optIdx, recipes, preferences);
  }

  // ─── PASS 4: Final rebalance per meal ───
  const finalExactBudgets = getNormalizedMealBudgets(macroTargets?.cal || targetCalories, activeMeals);
  for (const mealKey of activeMeals) {
    const mealBudget = finalExactBudgets[mealKey];
    for (let optIdx = 0; optIdx < allOptions[mealKey].length; optIdx++) {
      allOptions[mealKey][optIdx] = rebalanceMealToBudget(
        mealKey,
        allOptions[mealKey][optIdx] || [],
        mealBudget,
        recipes,
        preferences,
      );
    }
  }

  // ─── PASS 4.5: Hard cap after rebalance ───
  {
    const postRebalBudgets = getNormalizedMealBudgets(macroTargets?.cal || targetCalories, activeMeals);
    for (let optIdx = 0; optIdx < allOptions[activeMeals[0]]?.length; optIdx++) {
      clampMealsToBudgetCeiling(allOptions, activeMeals, optIdx, postRebalBudgets, recipes, preferences, 1.02);
    }
  }

  // ─── PASS 5: Re-tag free beverages (dedup may have rebuilt options) ───
  for (const mealKey of activeMeals) {
    for (let optIdx = 0; optIdx < allOptions[mealKey].length; optIdx++) {
      tagFreeBeverages(allOptions, [mealKey], optIdx, recipes);
    }
  }

  // ─── PASS 6: Calorie guard — uniform proportional adjustment for 99-101% daily total ───
  {
    const guardBudgets = getNormalizedMealBudgets(macroTargets?.cal || targetCalories, activeMeals);
    for (let optIdx = 0; optIdx < optionsCount && optIdx < (allOptions[activeMeals[0]]?.length || 0); optIdx++) {
      // First rebalance each meal to its budget
      nudgeMealCalories(allOptions, activeMeals, optIdx, macroTargets || { protein: 0, carbs: 0, fat: 0, cal: targetCalories }, recipes, preferences);
      // Then guard daily total — tighter tolerance (0.5%)
      calorieGuard(allOptions, activeMeals, optIdx, macroTargets?.cal || targetCalories, recipes, preferences, 0.005);
      // Hard cap per-meal (1.03) and re-tag
      clampMealsToBudgetCeiling(allOptions, activeMeals, optIdx, guardBudgets, recipes, preferences, 1.03);
      tagFreeBeverages(allOptions, activeMeals, optIdx, recipes);
    }
  }

  // ─── PASS 7: Ensure all active meals have exactly optionsCount non-empty options ───
  const finalBudgets = getNormalizedMealBudgets(macroTargets?.cal || targetCalories, activeMeals);
  for (const key of activeMeals) {
    // First, find best non-empty source option for cloning
    const nonEmptyOptions = allOptions[key].filter(opt => opt.length > 0);

    // Fill empty slots with diversified clones (swap items + scale variation)
    for (let oi = 0; oi < allOptions[key].length; oi++) {
      if (allOptions[key][oi].length === 0 && nonEmptyOptions.length > 0) {
        const source = nonEmptyOptions[oi % nonEmptyOptions.length];
        const blacklist = collectBlacklistedSignatures(allOptions[key], oi);
        const diversified = diversifyClonedOption(key, source, blacklist, recipes, preferences);
        allOptions[key][oi] = fitOptionToBudget(key, diversified, finalBudgets[key], recipes, preferences);
      }
    }

    // Pad to optionsCount with diversified clones
    while (allOptions[key].length < optionsCount) {
      if (nonEmptyOptions.length > 0) {
        const sourceIdx = (allOptions[key].length) % nonEmptyOptions.length;
        const newIdx = allOptions[key].length;
        allOptions[key].push([]); // placeholder so collectBlacklistedSignatures can exclude this slot
        const blacklist = collectBlacklistedSignatures(allOptions[key], newIdx);
        const diversified = diversifyClonedOption(key, nonEmptyOptions[sourceIdx], blacklist, recipes, preferences);
        allOptions[key][newIdx] = fitOptionToBudget(key, diversified, finalBudgets[key], recipes, preferences);
      } else {
        allOptions[key].push([]);
      }
    }
  }

  // ─── PASS 8: Final budget normalization after backfill/cloning ───
  for (const mealKey of activeMeals) {
    for (let optIdx = 0; optIdx < allOptions[mealKey].length; optIdx++) {
      allOptions[mealKey][optIdx] = fitOptionToBudget(
        mealKey,
        allOptions[mealKey][optIdx] || [],
        finalBudgets[mealKey],
        recipes,
        preferences,
      );
    }
  }
  ensureBreakfastFruitPresence(allOptions, finalBudgets, recipes, preferences);
  for (let optIdx = 0; optIdx < optionsCount && optIdx < (allOptions[activeMeals[0]]?.length || 0); optIdx++) {
    clampMealsToBudgetCeiling(allOptions, activeMeals, optIdx, finalBudgets, recipes, preferences, 1.02);
    tagFreeBeverages(allOptions, activeMeals, optIdx, recipes);
  }

  // ─── PASS 9: Safety net — every lunch/dinner option MUST have a protein source ───
  // If any option lacks protein, replace it with a varied clone of an option that has one.
  for (const mealKey of ["lunch", "dinner"] as MealKey[]) {
    if (!activeMeals.includes(mealKey)) continue;
    const opts = allOptions[mealKey];
    if (!opts || opts.length === 0) continue;

    const optionHasProtein = (option: string[]): boolean => {
      return option.some((scaledId) => {
        const { baseId } = safeParseScaledId(scaledId);
        const r = findRecipeById(baseId, recipes);
        return r ? recipeHasProteinSource(r) : false;
      });
    };

    const proteinIndices: number[] = [];
    const missingIndices: number[] = [];
    opts.forEach((opt, idx) => {
      if (optionHasProtein(opt)) proteinIndices.push(idx);
      else missingIndices.push(idx);
    });

    if (proteinIndices.length === 0) continue; // nothing to clone from
    for (const idx of missingIndices) {
      const sourceIdx = proteinIndices[idx % proteinIndices.length];
      const blacklist = collectBlacklistedSignatures(opts, idx);
      const diversified = diversifyClonedOption(mealKey, opts[sourceIdx], blacklist, recipes, preferences);
      opts[idx] = fitOptionToBudget(mealKey, diversified, finalBudgets[mealKey], recipes, preferences);
    }
  }

  // ─── PASS 10: FINAL HARD CONVERGENCE — guarantee 100% daily closure ───
  // After protein safety net, force every option's daily total to ≤ 100.1% of target
  // via uniform proportional scaling. This is the LAST line of defense.
  if (macroTargets || targetCalories > 0) {
    const dailyTarget = macroTargets?.cal || targetCalories;
    const optsLen = allOptions[activeMeals[0]]?.length || 0;
    for (let optIdx = 0; optIdx < optsLen; optIdx++) {
      // Re-balance per-meal budgets first
      nudgeMealCalories(
        allOptions,
        activeMeals,
        optIdx,
        macroTargets || { protein: 0, carbs: 0, fat: 0, cal: targetCalories },
        recipes,
        preferences,
      );
      // Tight calorie guard (0.5%) — bring within 99.5–100.5%
      calorieGuard(allOptions, activeMeals, optIdx, dailyTarget, recipes, preferences, 0.005);
      // Per-meal hard cap at 102%
      clampMealsToBudgetCeiling(allOptions, activeMeals, optIdx, finalBudgets, recipes, preferences, 1.02);
      // FINAL daily hard clamp — never above 100.1%
      hardDailyClamp(allOptions, activeMeals, optIdx, dailyTarget, recipes, preferences, 1.001);
      // Add items to meals with deficit when current items can't grow further
      addItemsToFillMealDeficit(allOptions, activeMeals, optIdx, finalBudgets, recipes, preferences, 0.90);
      // FINAL daily hard boost — never below 99.0% (mirror of clamp), respecting per-meal cap
      hardDailyBoost(allOptions, activeMeals, optIdx, dailyTarget, recipes, preferences, 0.999, finalBudgets);
      // Re-clamp in case boost pushed any meal above ceiling
      clampMealsToBudgetCeiling(allOptions, activeMeals, optIdx, finalBudgets, recipes, preferences, 1.02);
      hardDailyClamp(allOptions, activeMeals, optIdx, dailyTarget, recipes, preferences, 1.001);
      // HARD CEILING: ensure no snack/optional exceeds 110% (UI lock threshold).
      // enforceMealHardCeiling now also calls closeOptionToMealBudgetInPlace
      // after each removal to refill the meal locally.
      enforceMealHardCeiling(allOptions, activeMeals, optIdx, finalBudgets, recipes, preferences, 1.10);
      // EXACT MEAL CLOSURE — guarantee every editable meal lands within
      // [99%, 105%] of its budget (no big deficits after corrections).
      for (const mealKey of activeMeals) {
        closeOptionToMealBudgetInPlace(allOptions, mealKey, optIdx, finalBudgets, recipes, preferences);
      }
      // Daily reconciliation after per-meal closure.
      hardDailyBoost(allOptions, activeMeals, optIdx, dailyTarget, recipes, preferences, 0.999, finalBudgets);
      clampMealsToBudgetCeiling(allOptions, activeMeals, optIdx, finalBudgets, recipes, preferences, 1.10);
      hardDailyClamp(allOptions, activeMeals, optIdx, dailyTarget, recipes, preferences, 1.001);
      // Re-tag free beverages (last so the tag survives)
      tagFreeBeverages(allOptions, activeMeals, optIdx, recipes);
    }
  }

  // ─── PASS 11: FINAL DUPLICATION GUARD ───
  // Detect any remaining identical options (same set of base IDs, ignoring scale)
  // and diversify them by swapping items with role-compatible alternatives.
  for (const mealKey of activeMeals) {
    const opts = allOptions[mealKey];
    if (!opts || opts.length <= 1) continue;

    const seen = new Map<string, number>();
    for (let i = 0; i < opts.length; i++) {
      const sig = getOptionSignature(opts[i]);
      if (sig === "") continue;
      if (!seen.has(sig)) {
        seen.set(sig, i);
        continue;
      }
      // Duplicate detected — diversify this option
      const blacklist = collectBlacklistedSignatures(opts, i);
      const diversified = diversifyClonedOption(mealKey, opts[i], blacklist, recipes, preferences);
      const newSig = getOptionSignature(diversified);
      if (newSig !== sig && !blacklist.has(newSig)) {
        opts[i] = fitOptionToBudget(
          mealKey,
          diversified,
          finalBudgets[mealKey],
          recipes,
          preferences,
        );
        // Re-apply final convergence on this option
        const dailyTarget = macroTargets?.cal || targetCalories;
        if (dailyTarget > 0) {
          nudgeMealCalories(
            allOptions,
            activeMeals,
            i,
            macroTargets || { protein: 0, carbs: 0, fat: 0, cal: targetCalories },
            recipes,
            preferences,
          );
          calorieGuard(allOptions, activeMeals, i, dailyTarget, recipes, preferences, 0.005);
          clampMealsToBudgetCeiling(allOptions, activeMeals, i, finalBudgets, recipes, preferences, 1.02);
          hardDailyClamp(allOptions, activeMeals, i, dailyTarget, recipes, preferences, 1.001);
          addItemsToFillMealDeficit(allOptions, activeMeals, i, finalBudgets, recipes, preferences, 0.90);
          hardDailyBoost(allOptions, activeMeals, i, dailyTarget, recipes, preferences, 0.999, finalBudgets);
          clampMealsToBudgetCeiling(allOptions, activeMeals, i, finalBudgets, recipes, preferences, 1.02);
          hardDailyClamp(allOptions, activeMeals, i, dailyTarget, recipes, preferences, 1.001);
          enforceMealHardCeiling(allOptions, activeMeals, i, finalBudgets, recipes, preferences, 1.10);
          // EXACT MEAL CLOSURE per meal
          for (const mk of activeMeals) {
            closeOptionToMealBudgetInPlace(allOptions, mk, i, finalBudgets, recipes, preferences);
          }
          hardDailyBoost(allOptions, activeMeals, i, dailyTarget, recipes, preferences, 0.999, finalBudgets);
          clampMealsToBudgetCeiling(allOptions, activeMeals, i, finalBudgets, recipes, preferences, 1.10);
          hardDailyClamp(allOptions, activeMeals, i, dailyTarget, recipes, preferences, 1.001);
          tagFreeBeverages(allOptions, activeMeals, i, recipes);
        }
        seen.set(getOptionSignature(opts[i]), i);
      }
    }
  }

  // Build result
  for (const key of mealOrder) {
    if (allOptions[key].length > 0) {
      result[key] = allOptions[key];
    }
  }

  return result;
}

/** Get the number of options based on subscription plan */
export function getOptionsCount(plan: string | null): number {
  switch (plan) {
    case "basic":
    case "plus":
    case "complete":
      return 3;
    default:
      return 0;
  }
}
